import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Send as SendIcon,
  ViewColumn as ViewColumnIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import { apiFetch } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "/api/admin";

const AI_CHANGE_SCOPE_OPTIONS = [
  { value: "system_prompt", label: "System Prompt" },
  { value: "user_template", label: "User Prompt" },
  { value: "schema_json", label: "Output Schema" },
  { value: "model_config", label: "Model Config" },
];

const DEFAULT_AI_CHANGE_SCOPE = AI_CHANGE_SCOPE_OPTIONS.map((option) => option.value);
const AI_CONSTRAINT_SCOPE_VALUE = "constraints";

const AI_MAPPING_TYPE_OPTIONS = [
  { value: "Direct", label: "Direct" },
  { value: "Derived", label: "Derived" },
  { value: "DerivedContext", label: "Derived Context" },
];

const AI_CONSTRAINT_TYPE_OPTIONS = [
  { value: "Category", label: "Category" },
  { value: "Range", label: "Range" },
  { value: "Guidance", label: "Guidance" },
];

const AI_SCOPE_LABELS = {
  ...Object.fromEntries(AI_CHANGE_SCOPE_OPTIONS.map((option) => [option.value, option.label])),
  [AI_CONSTRAINT_SCOPE_VALUE]: "Constraints",
};

const truncateSummaryText = (value, maxLength = 180) => {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

const getApiErrorMessage = (error, fallback = "Request failed") => {
  if (!error) return fallback;
  if (typeof error === "string") return error.trim() || fallback;
  if (error.message) return error.message;
  if (error.error) return error.error;
  if (error.detail) return error.detail;
  try {
    return JSON.stringify(error);
  } catch {
    return fallback;
  }
};

const buildAiActionSummary = ({
  isRevision,
  effectiveChangeScope,
  schemaJsonChanged,
  nextDraft,
  nextConstraintDraftRows,
  aiConstraintsRequested,
  selectedAiMappingTypes,
  selectedAiConstraintTypes,
}) => {
  const scopeSet = new Set(effectiveChangeScope || []);
  const actions = [];

  if (scopeSet.has("system_prompt")) {
    actions.push(`${isRevision ? "Updated" : "Created"} the System Prompt.`);
  }
  if (scopeSet.has("user_template")) {
    actions.push(`${isRevision ? "Updated" : "Created"} the User Prompt.`);
  }
  if (scopeSet.has("schema_json")) {
    actions.push(
      isRevision && schemaJsonChanged === false
        ? "Reviewed the Output Schema, but no JSON change was returned."
        : `${isRevision ? "Updated" : "Created"} the Output Schema.`,
    );
  }
  if (scopeSet.has("model_config")) {
    actions.push(`${isRevision ? "Updated" : "Created"} the Model Config.`);
  }
  if (scopeSet.has(AI_CONSTRAINT_SCOPE_VALUE)) {
    const rowCount = nextConstraintDraftRows?.length || 0;
    const typeText =
      aiConstraintsRequested && selectedAiMappingTypes.length && selectedAiConstraintTypes.length
        ? ` using ${selectedAiMappingTypes.join(", ")} mapping and ${selectedAiConstraintTypes.join(", ")} constraint types`
        : "";
    actions.push(
      rowCount > 0
        ? `${isRevision ? "Updated" : "Created"} ${rowCount} constraint draft row${rowCount === 1 ? "" : "s"}${typeText}.`
        : `Prepared constraint generation${typeText}, but no draft rows were returned.`,
    );
  }

  const selectedScopeLabels = (effectiveChangeScope || [])
    .map((scopeValue) => AI_SCOPE_LABELS[scopeValue])
    .filter(Boolean);

  const lines = [
    isRevision ? "Action summary:" : "Created first draft:",
    ...actions.map((action) => `- ${action}`),
  ];

  if (nextDraft?.notes) {
    lines.push(`- Notes: ${truncateSummaryText(nextDraft.notes)}`);
  } else if (selectedScopeLabels.length > 0) {
    lines.push(`- Sections considered: ${selectedScopeLabels.join(", ")}.`);
  }

  return lines.join("\n");
};

const toBooleanFlag = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

const getBlankConstraintMasterForm = (
  tower = "",
  commodity = "",
  component = "",
  targetField = "",
) => ({
  tower,
  commodity,
  component,
  source_field: "",
  source_category: "",
  target_field: targetField,
  allowed_values: "",
  min_value: "",
  max_value: "",
  guidance_text: "",
  is_active: true,
  field_description: "",
  selection_guidance: "",
  decision_inputs: "",
  fallback_guidance: "",
  mapping_type: "",
  constraint_type: "",
});

const mapConstraintMasterItemToForm = (item) => ({
  tower: item.tower || "",
  commodity: item.commodity || "",
  component: item.component || "",
  source_field: item.source_field || "",
  source_category: item.source_category || "",
  target_field: item.target_field || "",
  allowed_values: item.allowed_values || "",
  min_value: item.min_value === null || item.min_value === undefined ? "" : String(item.min_value),
  max_value: item.max_value === null || item.max_value === undefined ? "" : String(item.max_value),
  guidance_text: item.guidance_text || "",
  is_active: !!item.is_active,
  field_description: item.field_description || "",
  selection_guidance: item.selection_guidance || "",
  decision_inputs: item.decision_inputs || "",
  fallback_guidance: item.fallback_guidance || "",
  mapping_type: item.mapping_type || inferMappingType(item),
  constraint_type: item.constraint_type || inferConstraintType(item),
});

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clonePlainValue = (value) => JSON.parse(JSON.stringify(value ?? null));

const buildRestorePointKey = (tower = "", commodity = "", schemaStage = "", schemaType = "") =>
  [tower || "", commodity || "", schemaStage || "", schemaType || ""].join("::");

const inferConstraintType = (row) => {
  if (row?.constraint_type) return row.constraint_type;
  const minValue = row?.min_value ?? row?.minValue;
  const maxValue = row?.max_value ?? row?.maxValue;
  const allowedValues = row?.allowed_values ?? row?.allowedValues;
  if (minValue !== null && minValue !== undefined) return "Range";
  if (maxValue !== null && maxValue !== undefined) return "Range";
  if (String(allowedValues || "").trim()) return "Category";
  return "Guidance";
};

const inferMappingType = (row) => {
  if (row?.mapping_type) return row.mapping_type;
  const sourceField = String(row?.source_field || row?.sourceField || "").trim();
  if (sourceField && sourceField !== "-") {
    return "Direct";
  }
  if (["CommonlyUsedIngredients", "RarelyUsedIngredients", "BannedIngredients"].includes(row?.target_field)) {
    return "DerivedContext";
  }
  return "Derived";
};

const buildConstraintRulePreview = (row) => {
  const constraintType = inferConstraintType(row);
  const targetField = row?.target_field || "Target";
  const guidance = row?.guidance_text || "";

  if (constraintType === "Category" && row?.allowed_values) {
    return `${targetField} must be one of: ${String(row.allowed_values).replaceAll(";", ", ")}${guidance ? `. ${guidance}` : ""}`;
  }
  if (constraintType === "Range") {
    const hasMin = row?.min_value !== null && row?.min_value !== undefined && row?.min_value !== "";
    const hasMax = row?.max_value !== null && row?.max_value !== undefined && row?.max_value !== "";
    if (hasMin && hasMax) return `${targetField} must be between ${row.min_value} and ${row.max_value}${guidance ? `. ${guidance}` : ""}`;
    if (hasMin) return `${targetField} must be at least ${row.min_value}${guidance ? `. ${guidance}` : ""}`;
    if (hasMax) return `${targetField} must be at most ${row.max_value}${guidance ? `. ${guidance}` : ""}`;
  }
  return `${targetField}: ${guidance || row?.selection_guidance || row?.field_description || ""}`;
};

const getInitialAiSchemaChatMessages = () => [
  {
    role: "assistant",
    content: "Loaded the current schema. Tell me what you want to modify.",
  },
];

const SchemaManagementV3Page = () => {
  const { user } = useAuth();
  // State management
  const [towers, setTowers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const [selectedSchemaStage, setSelectedSchemaStage] = useState("Constraints");
  const [schemaTypes, setSchemaTypes] = useState([]);
  const [selectedSchemaType, setSelectedSchemaType] = useState(null);
  const [schemaCache, setSchemaCache] = useState({}); // Cache all loaded schemas
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Schema content state
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPromptTemplate, setUserPromptTemplate] = useState("");
  const [outputSchema, setOutputSchema] = useState("");
  const [modelConfig, setModelConfig] = useState({
    model: "gpt-4o",
    temperature: 0.7,
    max_tokens: 4000,
  });
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [jsonPrompt, setJsonPrompt] = useState("");
  const [constraintsJson, setConstraintsJson] = useState("");
  const [returnToFrontend, setReturnToFrontend] = useState(false);

  // AI Refine state
  const [refineDialogOpen, setRefineDialogOpen] = useState(false);
  const [refineType, setRefineType] = useState(""); // "system" or "user"
  const [refining, setRefining] = useState(false);
  const [aiVariants, setAiVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentPromptText, setCurrentPromptText] = useState("");

  // Editor tabs (0=System Prompt, 1=User Prompt, 2=Output Schema, 3=Constraints, 4=Model Config)
  const [editorTab, setEditorTab] = useState(0);
  const [schemaViewMode, setSchemaViewMode] = useState("code"); // "code" or "card"

  // Versions and audit
  const [versions, setVersions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0=AI Assistant, 1=Versions
  const [schemaNavCollapsed, setSchemaNavCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [schemaRestorePoints, setSchemaRestorePoints] = useState({});
  const [selectedRestorePointId, setSelectedRestorePointId] = useState("");

  // Constraint Rules state (for Constraints tab)
  const [constraintRules, setConstraintRules] = useState([]);
  const [selectedConstraintRule, setSelectedConstraintRule] = useState(null);
  const [loadingConstraintRules, setLoadingConstraintRules] = useState(false);
  const [constraintViewMode, setConstraintViewMode] = useState("json"); // "json" or "card"
  const [editingSection, setEditingSection] = useState(null); // Track which section is being edited
  const [editedConstraints, setEditedConstraints] = useState({}); // Store edited constraint data
  const [editingRule, setEditingRule] = useState(false); // Track if editing entire rule
  const [editedRuleData, setEditedRuleData] = useState(null); // Store edited rule data
  const [editingJsonView, setEditingJsonView] = useState(false); // Track if editing JSON view
  const [editedJsonContent, setEditedJsonContent] = useState(""); // Store edited JSON content
  const [newRuleDialogOpen, setNewRuleDialogOpen] = useState(false);
  const [newRuleData, setNewRuleData] = useState({
    priority: 100,
    active: true,
    condition_json: {},
    constraints_json: {},
    notes: "",
  });

  // Constraint Master state (for Constraints tab)
  const [constraintMasterItems, setConstraintMasterItems] = useState([]);
  const [aiConstraintMasterDraftRows, setAiConstraintMasterDraftRows] = useState([]);
  const [loadingConstraintMaster, setLoadingConstraintMaster] = useState(false);
  const [savingConstraintMaster, setSavingConstraintMaster] = useState(false);
  const [constraintMasterBulkSavingKey, setConstraintMasterBulkSavingKey] = useState("");
  const [selectedConstraintComponent, setSelectedConstraintComponent] = useState("");
  const [selectedConstraintTarget, setSelectedConstraintTarget] = useState("");
  const [constraintComponentSearch, setConstraintComponentSearch] = useState("");
  const [constraintTargetSearch, setConstraintTargetSearch] = useState("");
  const [constraintMasterDialogOpen, setConstraintMasterDialogOpen] = useState(false);
  const [constraintMasterDialogMode, setConstraintMasterDialogMode] = useState("create");
  const [constraintMasterCreateType, setConstraintMasterCreateType] = useState("category");
  const [constraintMasterDialogItemId, setConstraintMasterDialogItemId] = useState(null);
  const [constraintMasterForm, setConstraintMasterForm] = useState(getBlankConstraintMasterForm());
  const [constraintMasterBaseline, setConstraintMasterBaseline] = useState(
    getBlankConstraintMasterForm(),
  );

  // Dialogs
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [versionDescription, setVersionDescription] = useState("");
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [versionToActivate, setVersionToActivate] = useState(null);
  const [createSchemaDialogOpen, setCreateSchemaDialogOpen] = useState(false);
  const [aiSchemaDialogOpen, setAiSchemaDialogOpen] = useState(false);
  const [duplicateSchemaDialogOpen, setDuplicateSchemaDialogOpen] = useState(false);
  const [renameSchemaDialogOpen, setRenameSchemaDialogOpen] = useState(false);
  const [newSchemaTypeName, setNewSchemaTypeName] = useState("");
  const [newSchemaTypeDescription, setNewSchemaTypeDescription] = useState("");
  const [aiSchemaMode, setAiSchemaMode] = useState("create");
  const [aiSchemaTypeName, setAiSchemaTypeName] = useState("");
  const [aiSchemaDescription, setAiSchemaDescription] = useState("");
  const [aiSchemaDraft, setAiSchemaDraft] = useState(null);
  const [aiSchemaTab, setAiSchemaTab] = useState(0);
  const [aiSchemaChatMessages, setAiSchemaChatMessages] = useState([]);
  const [aiSchemaGenerating, setAiSchemaGenerating] = useState(false);
  const [aiSchemaError, setAiSchemaError] = useState(null);
  const [aiChangeScope, setAiChangeScope] = useState(DEFAULT_AI_CHANGE_SCOPE);
  const [aiConstraintsEnabled, setAiConstraintsEnabled] = useState(false);
  const [selectedAiMappingTypes, setSelectedAiMappingTypes] = useState([]);
  const [selectedAiConstraintTypes, setSelectedAiConstraintTypes] = useState([]);
  const [aiSettingsExpanded, setAiSettingsExpanded] = useState(true);
  const [duplicateSourceSchemaType, setDuplicateSourceSchemaType] = useState("");
  const [duplicateTargetSchemaType, setDuplicateTargetSchemaType] = useState("");
  const [duplicateSchemaDescription, setDuplicateSchemaDescription] = useState("");
  const [renameSchemaTypeName, setRenameSchemaTypeName] = useState("");
  const [schemaTypeActionLoading, setSchemaTypeActionLoading] = useState(false);

  // Execution Group editing
  const [editingExecutionGroup, setEditingExecutionGroup] = useState(null); // schema_type being edited
  const [executionGroupValue, setExecutionGroupValue] = useState("");
  const [executionGroupDialogOpen, setExecutionGroupDialogOpen] = useState(false);
  const [tempSchemaGroups, setTempSchemaGroups] = useState([]);
  const [savingGroups, setSavingGroups] = useState(false);

  const normalizedRole = (() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "")
        .trim()
        .toLowerCase();
    }
    return String(user?.role || "")
      .trim()
      .toLowerCase();
  })();

  const isSuperAdmin = normalizedRole === "super admin";

  const constraintMasterDisplayItems = useMemo(
    () => [...constraintMasterItems, ...aiConstraintMasterDraftRows],
    [constraintMasterItems, aiConstraintMasterDraftRows],
  );

  const restorePointKey = useMemo(
    () =>
      buildRestorePointKey(
        selectedTower,
        selectedCommodity,
        selectedSchemaStage,
        selectedSchemaType || aiSchemaTypeName,
      ),
    [selectedTower, selectedCommodity, selectedSchemaStage, selectedSchemaType, aiSchemaTypeName],
  );

  const currentRestorePoints = useMemo(
    () => schemaRestorePoints[restorePointKey] || [],
    [schemaRestorePoints, restorePointKey],
  );

  const originalRestorePoint = useMemo(
    () => currentRestorePoints.find((point) => point.phase === "original") || null,
    [currentRestorePoints],
  );

  const firstUserMessageIndex = useMemo(
    () => aiSchemaChatMessages.findIndex((message) => message.role === "user"),
    [aiSchemaChatMessages],
  );

  const constraintComponentGroups = useMemo(() => {
    const counts = {};
    constraintMasterDisplayItems.forEach((item) => {
      const key = item.component || "Unknown";
      if (!counts[key]) {
        counts[key] = { count: 0, activeCount: 0, draftCount: 0 };
      }
      counts[key].count += 1;
      if (item.is_active) {
        counts[key].activeCount += 1;
      }
      if (item.__draft) {
        counts[key].draftCount += 1;
      }
    });

    return Object.entries(counts)
      .map(([component, summary]) => ({
        component,
        count: summary.count,
        activeCount: summary.activeCount,
        draftCount: summary.draftCount,
      }))
      .sort((a, b) => a.component.localeCompare(b.component));
  }, [constraintMasterDisplayItems]);

  const filteredConstraintComponentGroups = useMemo(() => {
    const searchValue = constraintComponentSearch.trim().toLowerCase();
    if (!searchValue) return constraintComponentGroups;
    return constraintComponentGroups.filter((group) =>
      group.component.toLowerCase().includes(searchValue),
    );
  }, [constraintComponentGroups, constraintComponentSearch]);

  const constraintTargetGroups = useMemo(() => {
    if (!selectedConstraintComponent) return [];

    const counts = {};
    constraintMasterDisplayItems
      .filter((item) => (item.component || "Unknown") === selectedConstraintComponent)
      .forEach((item) => {
        const key = item.target_field || "Unknown";
        if (!counts[key]) {
          counts[key] = { count: 0, activeCount: 0, draftCount: 0 };
        }
        counts[key].count += 1;
        if (item.is_active) {
          counts[key].activeCount += 1;
        }
        if (item.__draft) {
          counts[key].draftCount += 1;
        }
      });

    return Object.entries(counts)
      .map(([targetField, summary]) => ({
        targetField,
        count: summary.count,
        activeCount: summary.activeCount,
        draftCount: summary.draftCount,
      }))
      .sort((a, b) => a.targetField.localeCompare(b.targetField));
  }, [constraintMasterDisplayItems, selectedConstraintComponent]);

  const filteredConstraintTargetGroups = useMemo(() => {
    const searchValue = constraintTargetSearch.trim().toLowerCase();
    if (!searchValue) return constraintTargetGroups;
    return constraintTargetGroups.filter((group) =>
      group.targetField.toLowerCase().includes(searchValue),
    );
  }, [constraintTargetGroups, constraintTargetSearch]);

  const constraintAccordionGroups = useMemo(() => {
    const searchValue = constraintComponentSearch.trim().toLowerCase();

    return constraintComponentGroups
      .map((componentGroup) => {
        const targetCounts = {};
        constraintMasterDisplayItems
          .filter((item) => (item.component || "Unknown") === componentGroup.component)
          .forEach((item) => {
            const key = item.target_field || "Unknown";
            if (!targetCounts[key]) {
              targetCounts[key] = { count: 0, activeCount: 0, draftCount: 0 };
            }
            targetCounts[key].count += 1;
            if (item.is_active) {
              targetCounts[key].activeCount += 1;
            }
            if (item.__draft) {
              targetCounts[key].draftCount += 1;
            }
          });

        const targetGroups = Object.entries(targetCounts)
          .map(([targetField, summary]) => ({
            targetField,
            count: summary.count,
            activeCount: summary.activeCount,
            draftCount: summary.draftCount,
          }))
          .sort((a, b) => a.targetField.localeCompare(b.targetField));

        const componentMatches = componentGroup.component.toLowerCase().includes(searchValue);
        const visibleTargetGroups =
          !searchValue || componentMatches
            ? targetGroups
            : targetGroups.filter((group) =>
                group.targetField.toLowerCase().includes(searchValue),
              );

        if (searchValue && !componentMatches && visibleTargetGroups.length === 0) {
          return null;
        }

        return {
          ...componentGroup,
          targetGroups: visibleTargetGroups,
          totalTargetCount: targetGroups.length,
        };
      })
      .filter(Boolean);
  }, [constraintComponentGroups, constraintMasterDisplayItems, constraintComponentSearch]);

  const constraintMasterTableRows = useMemo(() => {
    if (!selectedConstraintComponent || !selectedConstraintTarget) return [];
    return constraintMasterDisplayItems
      .filter(
        (item) =>
          (item.component || "Unknown") === selectedConstraintComponent &&
          (item.target_field || "Unknown") === selectedConstraintTarget,
      )
      .sort((a, b) => {
        const categoryCompare = String(a.source_category || "").localeCompare(
          String(b.source_category || ""),
        );
        if (categoryCompare !== 0) return categoryCompare;
        const aId = Number(a.id);
        const bId = Number(b.id);
        return (Number.isFinite(aId) ? aId : 0) - (Number.isFinite(bId) ? bId : 0);
      });
  }, [constraintMasterDisplayItems, selectedConstraintComponent, selectedConstraintTarget]);

  const selectedConstraintComponentRows = useMemo(() => {
    if (!selectedConstraintComponent) return [];
    return constraintMasterDisplayItems.filter(
      (item) => (item.component || "Unknown") === selectedConstraintComponent,
    );
  }, [constraintMasterDisplayItems, selectedConstraintComponent]);

  const selectedConstraintComponentActiveCount = useMemo(
    () => selectedConstraintComponentRows.filter((item) => !!item.is_active).length,
    [selectedConstraintComponentRows],
  );

  const selectedConstraintTargetActiveCount = useMemo(
    () => constraintMasterTableRows.filter((item) => !!item.is_active).length,
    [constraintMasterTableRows],
  );

  const constraintMasterDialogIsDirty = useMemo(
    () => JSON.stringify(constraintMasterForm) !== JSON.stringify(constraintMasterBaseline),
    [constraintMasterForm, constraintMasterBaseline],
  );

  const aiConstraintComponentName =
    selectedSchemaType || aiSchemaTypeName || selectedConstraintComponent || "";

  const aiConstraintExampleRows = useMemo(() => {
    const componentName = aiConstraintComponentName.trim().toLowerCase();
    return constraintMasterItems
      .filter((row) => {
        if (!componentName) return true;
        if (String(row.component || "").trim().toLowerCase() !== componentName) return false;
        const mappingType = inferMappingType(row);
        const constraintType = inferConstraintType(row);
        const mappingMatches =
          selectedAiMappingTypes.length === 0 || selectedAiMappingTypes.includes(mappingType);
        const constraintMatches =
          selectedAiConstraintTypes.length === 0 || selectedAiConstraintTypes.includes(constraintType);
        return mappingMatches && constraintMatches;
      })
      .map((row) => ({
        ...row,
        mapping_type: inferMappingType(row),
        constraint_type: inferConstraintType(row),
      }))
      .sort((a, b) => {
        const targetCompare = String(a.target_field || "").localeCompare(String(b.target_field || ""));
        if (targetCompare !== 0) return targetCompare;
        return String(a.source_category || "").localeCompare(String(b.source_category || ""));
      });
  }, [
    constraintMasterItems,
    aiConstraintComponentName,
    selectedAiMappingTypes,
    selectedAiConstraintTypes,
  ]);

  const aiConstraintsRequested =
    aiConstraintsEnabled &&
    selectedAiMappingTypes.length > 0 &&
    selectedAiConstraintTypes.length > 0;

  useEffect(() => {
    setSelectedRestorePointId("");
  }, [restorePointKey]);

  // Load towers on mount
  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchTowers();
  }, [isSuperAdmin]);

  // Load commodities when tower changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower) {
      setCommodities([]);
      setSelectedCommodity(null);
      setSchemaTypes([]);
      setSelectedSchemaType(null);
      fetchCommodities(selectedTower);
    }
  }, [selectedTower, isSuperAdmin]);

  // Load schema types when commodity changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower && selectedCommodity) {
      setSchemaTypes([]);
      setSelectedSchemaType(null);
      setSchemaCache({});
      setVersions([]);
      setAuditLog([]);
      fetchSchemaTypes();
    }
  }, [selectedTower, selectedCommodity, selectedSchemaStage, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;

    setConstraintMasterItems([]);
    setAiConstraintMasterDraftRows([]);
    setSelectedConstraintComponent("");
    setSelectedConstraintTarget("");
    setConstraintComponentSearch("");
    setConstraintTargetSearch("");
    setConstraintMasterDialogOpen(false);

    if (selectedTower && selectedCommodity) {
      fetchConstraintMasterItems(selectedTower, selectedCommodity);
    }
  }, [selectedTower, selectedCommodity, isSuperAdmin]);

  useEffect(() => {
    if (!selectedTower || !selectedCommodity) return;

    const schemaComponent = selectedSchemaType || "";
    if (!schemaComponent) {
      setSelectedConstraintComponent("");
      setSelectedConstraintTarget("");
      return;
    }

    if (selectedConstraintComponent !== schemaComponent) {
      setSelectedConstraintComponent(schemaComponent);
    }

    const schemaComponentRows = constraintMasterDisplayItems.filter(
      (item) => (item.component || "Unknown") === schemaComponent,
    );

    if (schemaComponentRows.length === 0) {
      setSelectedConstraintTarget("");
      return;
    }

    const selectedTargetExists =
      selectedConstraintTarget &&
      schemaComponentRows.some(
        (item) => (item.target_field || "Unknown") === selectedConstraintTarget,
      );

    if (!selectedTargetExists) {
      setSelectedConstraintTarget(schemaComponentRows[0]?.target_field || "");
    }
  }, [
    constraintMasterDisplayItems,
    selectedTower,
    selectedCommodity,
    selectedSchemaType,
    selectedConstraintComponent,
    selectedConstraintTarget,
  ]);

  // API calls
  const fetchTowers = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/towers`);
      const data = await response.json();
      setTowers(data.towers || []);
    } catch (error) {
      setError("Failed to load towers");
    }
  };

  const fetchCommodities = async (tower) => {
    try {
      const response = await apiFetch(`${API_BASE}/commodities?tower=${tower}`);
      const data = await response.json();
      setCommodities(data.commodities || []);
    } catch (error) {
      setError("Failed to load commodities");
    }
  };

  const fetchSchemaTypes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tower: selectedTower,
        commodity: selectedCommodity,
      });
      if (selectedSchemaStage) {
        params.set("schema_stage", selectedSchemaStage);
      }
      const response = await apiFetch(
        `${API_BASE}/schema-v3/types?${params.toString()}`,
      );
      const data = await response.json();
      const types = data.schema_types || [];

      // Sort by execution_group in ascending order
      const sortedTypes = types.sort((a, b) => {
        const groupA = a.execution_group || 999;
        const groupB = b.execution_group || 999;
        return groupA - groupB;
      });

      setSchemaTypes(sortedTypes);

      // Preload all schemas into cache
      const cache = {};
      const stageQuery = selectedSchemaStage
        ? `?schema_stage=${encodeURIComponent(selectedSchemaStage)}`
        : "";
      await Promise.all(
        types.map(async (schemaType) => {
          try {
            const schemaResponse = await apiFetch(
              `${API_BASE}/schema-v3/${selectedTower}/${selectedCommodity}/${schemaType.schema_type}${stageQuery}`,
            );
            const schemaData = await schemaResponse.json();
            if (schemaData.success) {
              cache[schemaType.schema_type] = schemaData.schema;
            }
          } catch (err) {
            console.error(`Failed to preload ${schemaType.schema_type}:`, err);
          }
        }),
      );
      setSchemaCache(cache);
      setError(null);
    } catch (error) {
      setError("Failed to load schema types");
    } finally {
      setLoading(false);
    }
  };

  const fetchConstraintMasterItems = async (tower, commodity) => {
    if (!tower || !commodity) return [];

    setLoadingConstraintMaster(true);
    try {
      const params = new URLSearchParams({ tower, commodity });
      const response = await apiFetch(`${API_BASE}/constraint-master?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load constraint master rows");
      }

      const list = data.items || [];
      setConstraintMasterItems(list);
      setError(null);
      return list;
    } catch (fetchError) {
      setConstraintMasterItems([]);
      setError(fetchError.message || "Failed to load constraint master rows");
      return [];
    } finally {
      setLoadingConstraintMaster(false);
    }
  };

  const handleConstraintComponentSelect = (componentName) => {
    setSelectedConstraintComponent(componentName);
    const firstTargetForComponent =
      constraintMasterDisplayItems.find((item) => (item.component || "Unknown") === componentName)
        ?.target_field || "";
    setSelectedConstraintTarget(firstTargetForComponent);
  };

  const handleConstraintTargetSelect = (targetField) => {
    setSelectedConstraintTarget(targetField);
  };

  const handleConstraintMasterFieldChange = (field, value) => {
    setConstraintMasterForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleConstraintMasterOpenCreate = () => {
    if (!selectedTower || !selectedCommodity || !selectedConstraintComponent) {
      setError("Select a component before adding a category");
      return;
    }

    const blank = getBlankConstraintMasterForm(
      selectedTower,
      selectedCommodity,
      selectedConstraintComponent,
      selectedConstraintTarget || "",
    );
    setConstraintMasterDialogMode("create");
    setConstraintMasterCreateType("category");
    setConstraintMasterDialogItemId(null);
    setConstraintMasterForm(blank);
    setConstraintMasterBaseline(blank);
    setConstraintMasterDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleConstraintMasterOpenCreateTarget = () => {
    if (!selectedTower || !selectedCommodity || !selectedConstraintComponent) {
      setError("Select a component before adding a target column");
      return;
    }

    const blank = getBlankConstraintMasterForm(
      selectedTower,
      selectedCommodity,
      selectedConstraintComponent,
      "",
    );
    setConstraintMasterDialogMode("create");
    setConstraintMasterCreateType("target");
    setConstraintMasterDialogItemId(null);
    setConstraintMasterForm(blank);
    setConstraintMasterBaseline(blank);
    setConstraintMasterDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleConstraintMasterOpenEdit = (row) => {
    const mapped = mapConstraintMasterItemToForm(row);
    setConstraintMasterDialogMode("edit");
    setConstraintMasterCreateType("category");
    setConstraintMasterDialogItemId(row.id);
    setConstraintMasterForm(mapped);
    setConstraintMasterBaseline(mapped);
    setConstraintMasterDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleConstraintMasterDialogClose = () => {
    if (savingConstraintMaster) return;
    setConstraintMasterDialogOpen(false);
  };

  const handleConstraintMasterDialogSave = async () => {
    if (
      !constraintMasterForm.tower ||
      !constraintMasterForm.commodity ||
      !constraintMasterForm.component ||
      !constraintMasterForm.target_field ||
      !constraintMasterForm.source_category
    ) {
      setError("Component, target field, and source category are required");
      return;
    }

    const payload = {
      tower: constraintMasterForm.tower,
      commodity: constraintMasterForm.commodity,
      component: constraintMasterForm.component,
      source_field: constraintMasterForm.source_field,
      source_category: constraintMasterForm.source_category,
      target_field: constraintMasterForm.target_field,
      allowed_values: constraintMasterForm.allowed_values,
      min_value: toNumberOrNull(constraintMasterForm.min_value),
      max_value: toNumberOrNull(constraintMasterForm.max_value),
      guidance_text: constraintMasterForm.guidance_text,
      is_active: !!constraintMasterForm.is_active,
      field_description: constraintMasterForm.field_description,
      selection_guidance: constraintMasterForm.selection_guidance,
      decision_inputs: constraintMasterForm.decision_inputs,
      fallback_guidance: constraintMasterForm.fallback_guidance,
      mapping_type: constraintMasterForm.mapping_type || inferMappingType(constraintMasterForm),
      constraint_type: constraintMasterForm.constraint_type || inferConstraintType(constraintMasterForm),
    };

    setSavingConstraintMaster(true);
    setError(null);
    setSuccess(null);

    try {
      const url =
        constraintMasterDialogMode === "edit" && constraintMasterDialogItemId
          ? `${API_BASE}/constraint-master/${constraintMasterDialogItemId}`
          : `${API_BASE}/constraint-master`;
      const method =
        constraintMasterDialogMode === "edit" && constraintMasterDialogItemId ? "PUT" : "POST";

      const response = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to save constraint master item");
      }

      await fetchConstraintMasterItems(selectedTower, selectedCommodity);
      setSelectedConstraintComponent(payload.component);
      setSelectedConstraintTarget(payload.target_field);
      setConstraintMasterDialogOpen(false);
      setSuccess(
        constraintMasterDialogMode === "edit"
          ? "Constraint category updated"
          : constraintMasterCreateType === "target"
            ? "Target column created"
            : "Constraint category created",
      );
    } catch (saveError) {
      setError(getApiErrorMessage(saveError, "Failed to save constraint master item"));
    } finally {
      setSavingConstraintMaster(false);
    }
  };

  const handleConstraintMasterBulkStatusUpdate = async (scope, isActive, overrides = {}) => {
    const componentName = overrides.component || selectedConstraintComponent;
    const targetFieldName = overrides.targetField || selectedConstraintTarget;

    if (!selectedTower || !selectedCommodity || !componentName) {
      setError("Select a component first");
      return;
    }
    if (scope === "target" && !targetFieldName) {
      setError("Select a target column first");
      return;
    }

    const nextBulkSavingKey =
      scope === "target" ? `target:${componentName}:${targetFieldName}` : `component:${componentName}`;
    setConstraintMasterBulkSavingKey(nextBulkSavingKey);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`${API_BASE}/constraint-master/bulk-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          component: componentName,
          target_field: scope === "target" ? targetFieldName : undefined,
          scope,
          is_active: isActive,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update constraint status");
      }

      await fetchConstraintMasterItems(selectedTower, selectedCommodity);
      setSuccess(
        scope === "target"
          ? `Target column "${targetFieldName}" marked ${isActive ? "active" : "inactive"}`
          : `Component "${componentName}" marked ${isActive ? "active" : "inactive"}`,
      );
    } catch (saveError) {
      setError(getApiErrorMessage(saveError, "Failed to update constraint status"));
    } finally {
      setConstraintMasterBulkSavingKey("");
    }
  };

  const getConstraintMasterRowKey = (row) =>
    [
      row.tower || selectedTower,
      row.commodity || selectedCommodity,
      row.component || "",
      row.target_field || "",
      row.source_category || "",
    ]
      .map((value) => String(value).trim().toLowerCase())
      .join("|");

  const normalizeConstraintDelimitedValues = (value) =>
    String(value || "")
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean)
      .filter((item, index, list) => {
        const normalized = item.toLowerCase();
        return list.findIndex((candidate) => candidate.toLowerCase() === normalized) === index;
      })
      .join(";");

  const mergeConstraintMasterPayloadRows = (rows) => {
    const rowsByKey = new Map();

    rows.forEach((row) => {
      const normalizedRow = {
        ...row,
        tower: String(row.tower || "").trim(),
        commodity: String(row.commodity || "").trim(),
        component: String(row.component || "").trim(),
        source_field: String(row.source_field || "").trim(),
        source_category: String(row.source_category || "").trim(),
        target_field: String(row.target_field || "").trim(),
        allowed_values: normalizeConstraintDelimitedValues(row.allowed_values),
        guidance_text: String(row.guidance_text || "").trim(),
        field_description: String(row.field_description || "").trim(),
        selection_guidance: String(row.selection_guidance || "").trim(),
        decision_inputs: String(row.decision_inputs || "").trim(),
        fallback_guidance: String(row.fallback_guidance || "").trim(),
      };
      const rowKey = getConstraintMasterRowKey(normalizedRow);
      const existingRow = rowsByKey.get(rowKey);

      if (!existingRow) {
        rowsByKey.set(rowKey, normalizedRow);
        return;
      }

      rowsByKey.set(rowKey, {
        ...existingRow,
        ...normalizedRow,
        min_value: normalizedRow.min_value ?? existingRow.min_value,
        max_value: normalizedRow.max_value ?? existingRow.max_value,
        mapping_type: normalizedRow.mapping_type || existingRow.mapping_type,
        constraint_type: normalizedRow.constraint_type || existingRow.constraint_type,
        allowed_values: normalizeConstraintDelimitedValues(
          [existingRow.allowed_values, normalizedRow.allowed_values].filter(Boolean).join(";"),
        ),
        guidance_text: normalizedRow.guidance_text || existingRow.guidance_text,
        field_description: normalizedRow.field_description || existingRow.field_description,
        selection_guidance: normalizedRow.selection_guidance || existingRow.selection_guidance,
        decision_inputs: normalizedRow.decision_inputs || existingRow.decision_inputs,
        fallback_guidance: normalizedRow.fallback_guidance || existingRow.fallback_guidance,
      });
    });

    return Array.from(rowsByKey.values());
  };

  const buildConstraintMasterPayloadFromDraftRow = (row) => ({
    tower: row.tower || selectedTower,
    commodity: row.commodity || selectedCommodity,
    component: row.component || selectedSchemaType,
    source_field: row.source_field || row.sourceField || "",
    source_category: row.source_category || "",
    target_field: row.target_field || "",
    allowed_values: row.allowed_values || "",
    min_value: toNumberOrNull(row.min_value),
    max_value: toNumberOrNull(row.max_value),
    guidance_text: row.guidance_text || "",
    is_active: row.is_active === undefined ? true : !!row.is_active,
    field_description: row.field_description || "",
    selection_guidance: row.selection_guidance || "",
    decision_inputs: row.decision_inputs || "",
    fallback_guidance: row.fallback_guidance || "",
    mapping_type: row.mapping_type || inferMappingType(row),
    constraint_type: row.constraint_type || inferConstraintType(row),
  });

  const saveAiConstraintMasterDraftRows = async () => {
    const validDraftRows = mergeConstraintMasterPayloadRows(
      aiConstraintMasterDraftRows
        .map(buildConstraintMasterPayloadFromDraftRow)
        .filter(
          (row) =>
            row.tower &&
            row.commodity &&
            row.component &&
            row.target_field &&
            row.source_category,
        ),
    );

    if (validDraftRows.length === 0) return 0;

    const existingRowsByKey = new Map(
      constraintMasterItems.map((row) => [getConstraintMasterRowKey(row), row]),
    );

    for (const row of validDraftRows) {
      const rowKey = getConstraintMasterRowKey(row);
      const existingRow = existingRowsByKey.get(rowKey);
      const response = await apiFetch(
        existingRow?.id ? `${API_BASE}/constraint-master/${existingRow.id}` : `${API_BASE}/constraint-master`,
        {
          method: existingRow?.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(row),
        },
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || "Failed to save AI constraint draft row");
      }

      if (!existingRow?.id && data.id) {
        existingRowsByKey.set(rowKey, { ...row, id: data.id });
      }
    }

    const firstDraftRow = validDraftRows[0];
    await fetchConstraintMasterItems(selectedTower, selectedCommodity);
    setAiConstraintMasterDraftRows([]);
    setAiSchemaDraft((previousDraft) =>
      previousDraft
        ? {
            ...previousDraft,
            constraints: {
              ...(previousDraft.constraints || {}),
              master_rows: [],
            },
          }
        : previousDraft,
    );
    setSelectedConstraintComponent(firstDraftRow.component);
    setSelectedConstraintTarget(firstDraftRow.target_field);
    return validDraftRows.length;
  };

  const loadSchemaType = async (schemaType) => {
    try {
      // Check if schema is already in cache
      const cachedSchema = schemaCache[schemaType];

      if (cachedSchema) {
        // Use cached data - no loading indicator needed
        setSelectedSchemaType(schemaType);
        setSystemPrompt(cachedSchema.system_prompt || "");
        setUserPromptTemplate(cachedSchema.user_template || "");
        setOutputSchema(
          typeof cachedSchema.schema_json === "string"
            ? cachedSchema.schema_json
            : JSON.stringify(cachedSchema.schema_json, null, 2),
        );
        setModelConfig({
          model: cachedSchema.model || "gpt-4o",
          temperature: cachedSchema.temperature || 0.7,
          max_tokens: cachedSchema.max_tokens || 4000,
        });
        setDescription(cachedSchema.description || "");
        setNotes(cachedSchema.notes || "");
        setJsonPrompt(cachedSchema.image_prompt_json || "");
        setConstraintsJson(cachedSchema.constraints_json || "");
        setReturnToFrontend(toBooleanFlag(cachedSchema.return_to_frontend));
        if (activeTab === 0) {
          loadAiDraftFromSchema(schemaType, cachedSchema);
        }

        // Load versions and audit log in background
        loadVersions(schemaType);
        loadAuditLog(schemaType);

        // Refresh Constraint Master data if Constraints tab is currently active
        if (editorTab === 3) {
          fetchConstraintMasterItems(selectedTower, selectedCommodity);
        }

        setError(null);
      } else {
        // Fallback: fetch from server if not in cache
        setLoading(true);
        const stageQuery = selectedSchemaStage
          ? `?schema_stage=${encodeURIComponent(selectedSchemaStage)}`
          : "";
        const response = await apiFetch(
          `${API_BASE}/schema-v3/${selectedTower}/${selectedCommodity}/${schemaType}${stageQuery}`,
        );
        const data = await response.json();

        if (data.success) {
          // Update cache
          setSchemaCache((prev) => ({ ...prev, [schemaType]: data.schema }));

          setSelectedSchemaType(schemaType);
          setSystemPrompt(data.schema.system_prompt || "");
          setUserPromptTemplate(data.schema.user_template || "");
          setOutputSchema(
            typeof data.schema.schema_json === "string"
              ? data.schema.schema_json
              : JSON.stringify(data.schema.schema_json, null, 2),
          );
          setModelConfig({
            model: data.schema.model || "gpt-4o",
            temperature: data.schema.temperature || 0.7,
            max_tokens: data.schema.max_tokens || 4000,
          });
          setDescription(data.schema.description || "");
          setNotes(data.schema.notes || "");
          setJsonPrompt(data.schema.image_prompt_json || "");
          setConstraintsJson(data.schema.constraints_json || "");
          setReturnToFrontend(toBooleanFlag(data.schema.return_to_frontend));
          if (activeTab === 0) {
            loadAiDraftFromSchema(schemaType, data.schema);
          }

          loadVersions(schemaType);
          loadAuditLog(schemaType);

          // Refresh Constraint Master data if Constraints tab is currently active
          if (editorTab === 3) {
            fetchConstraintMasterItems(selectedTower, selectedCommodity);
          }

          setError(null);
        }
        setLoading(false);
      }
    } catch (error) {
      setError(`Failed to load schema: ${error.message}`);
      setLoading(false);
    }
  };

  const loadVersions = async (schemaType) => {
    try {
      const stageQuery = selectedSchemaStage
        ? `?schema_stage=${encodeURIComponent(selectedSchemaStage)}`
        : "";
      const response = await apiFetch(
        `${API_BASE}/schema-v3/${selectedTower}/${selectedCommodity}/${schemaType}/versions${stageQuery}`,
      );
      const data = await response.json();
      setVersions(data.versions || []);
    } catch (error) {
      console.error("Failed to load versions:", error);
    }
  };

  const loadAuditLog = async (schemaType) => {
    try {
      const stageQuery = selectedSchemaStage
        ? `?schema_stage=${encodeURIComponent(selectedSchemaStage)}`
        : "";
      const response = await apiFetch(
        `${API_BASE}/schema-v3/${selectedTower}/${selectedCommodity}/${schemaType}/audit${stageQuery}`,
      );
      const data = await response.json();
      setAuditLog(data.audit_log || []);
    } catch (error) {
      console.error("Failed to load audit log:", error);
    }
  };

  const updateExecutionGroup = async (schemaType, newGroup) => {
    try {
      const stageQuery = selectedSchemaStage
        ? `?schema_stage=${encodeURIComponent(selectedSchemaStage)}`
        : "";
      const response = await apiFetch(
        `${API_BASE}/schema-v3/${selectedTower}/${selectedCommodity}/${schemaType}/execution-group${stageQuery}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ execution_group: parseInt(newGroup) }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setSuccess("Execution group updated successfully");
        // Refresh schema types to get new sorting
        await fetchSchemaTypes();
        setEditingExecutionGroup(null);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || "Failed to update execution group");
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      setError(`Failed to update execution group: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleExecutionGroupClick = (schemaType, currentGroup) => {
    setEditingExecutionGroup(schemaType);
    setExecutionGroupValue(currentGroup?.toString() || "");
  };

  const handleExecutionGroupSave = async (schemaType) => {
    if (executionGroupValue && !isNaN(executionGroupValue)) {
      await updateExecutionGroup(schemaType, executionGroupValue);
    } else {
      setEditingExecutionGroup(null);
    }
  };

  const handleExecutionGroupKeyPress = (e, schemaType) => {
    if (e.key === "Enter") {
      handleExecutionGroupSave(schemaType);
    } else if (e.key === "Escape") {
      setEditingExecutionGroup(null);
    }
  };

  const openExecutionGroupDialog = () => {
    // Create a copy of schemaTypes with editable groups
    const groups = schemaTypes.map((s, index) => ({
      schema_type: s.schema_type,
      execution_group: s.execution_group || 999,
      original_order: index,
      description: s.description || "",
    }));
    setTempSchemaGroups(groups);
    setExecutionGroupDialogOpen(true);
  };

  const handleGroupChange = (schemaType, newGroup) => {
    setTempSchemaGroups((prev) =>
      prev.map((s) =>
        s.schema_type === schemaType ? { ...s, execution_group: parseInt(newGroup) || 1 } : s,
      ),
    );
  };

  const moveSchemaUp = (index) => {
    if (index === 0) return;
    setTempSchemaGroups((prev) => {
      const newGroups = [...prev];
      [newGroups[index - 1], newGroups[index]] = [newGroups[index], newGroups[index - 1]];
      return newGroups;
    });
  };

  const moveSchemaDown = (index) => {
    if (index === tempSchemaGroups.length - 1) return;
    setTempSchemaGroups((prev) => {
      const newGroups = [...prev];
      [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];
      return newGroups;
    });
  };

  const autoAssignGroups = () => {
    // Assign sequential groups based on current order
    const groupSize = Math.ceil(tempSchemaGroups.length / 5); // Divide into ~5 groups
    setTempSchemaGroups((prev) =>
      prev.map((s, index) => ({
        ...s,
        execution_group: Math.floor(index / groupSize) + 1,
      })),
    );
  };

  const saveExecutionGroups = async () => {
    setSavingGroups(true);
    try {
      const stageQuery = selectedSchemaStage
        ? `?schema_stage=${encodeURIComponent(selectedSchemaStage)}`
        : "";
      // Send updates to backend for each schema
      const updates = tempSchemaGroups.map(async (schema) => {
        const response = await apiFetch(
          `${API_BASE}/schema-v3/${selectedTower}/${selectedCommodity}/${schema.schema_type}/execution-group${stageQuery}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ execution_group: schema.execution_group }),
          },
        );

        if (!response.ok) {
          const text = await response.text();
          console.error(`Failed to update ${schema.schema_type}:`, text);
          throw new Error(`Failed to update ${schema.schema_type}: ${response.status}`);
        }

        return response.json();
      });

      const results = await Promise.all(updates);
      console.log("All updates completed:", results);

      setSuccess("Execution groups updated successfully");
      setExecutionGroupDialogOpen(false);

      // Refresh schema types to get new sorting
      await fetchSchemaTypes();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error updating execution groups:", error);
      setError(`Failed to update execution groups: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSavingGroups(false);
    }
  };

  const getNextSchemaTypeName = () => {
    const existingNames = new Set(schemaTypes.map((schema) => schema.schema_type.toLowerCase()));
    const baseName = "NewSchema";
    if (!existingNames.has(baseName.toLowerCase())) return baseName;

    let index = 1;
    while (existingNames.has(`${baseName}${index}`.toLowerCase())) {
      index += 1;
    }
    return `${baseName}${index}`;
  };

  const openCreateSchemaDialog = async () => {
    if (!selectedTower || !selectedCommodity || schemaTypeActionLoading) return;

    try {
      setSchemaTypeActionLoading(true);
      setError(null);
      setSuccess(null);

      const schemaTypeName = getNextSchemaTypeName();
      const response = await apiFetch(`${API_BASE}/schema-v3/create-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_stage: selectedSchemaStage,
          schema_type: schemaTypeName,
          schema_json: {},
          system_prompt: "",
          user_template: "",
          model: "gpt-4o",
          temperature: 0.2,
          max_tokens: 4000,
          description: "New schema draft",
          notes: "",
          return_to_frontend: selectedSchemaStage === "OutputSchema",
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to create schema type");
      }

      setActiveTab(0);
      await fetchSchemaTypes();
      await loadSchemaType(schemaTypeName);
      loadAiDraftFromSchema(schemaTypeName, {
        schema_json: {},
        system_prompt: "",
        user_template: "",
        model: "gpt-4o",
        temperature: 0.2,
        max_tokens: 4000,
        description: "New schema draft",
        notes: "",
        return_to_frontend: selectedSchemaStage === "OutputSchema",
      });
      setSuccess(`Schema '${schemaTypeName}' created. Rename it and use AI Assistant to build it.`);
    } catch (error) {
      setError(error.message || "Failed to create schema type");
    } finally {
      setSchemaTypeActionLoading(false);
    }
  };

  const createRestoreSnapshotFromDraft = (draft, options = {}) => {
    const schemaTypeName =
      options.schemaTypeName || selectedSchemaType || aiSchemaTypeName || "";
    const constraintDraftRows = options.constraintDraftRows || [];
    const chatMessages = options.chatMessages || [];

    return {
      systemPrompt: draft.system_prompt || "",
      userPromptTemplate: draft.user_template || "",
      outputSchema:
        typeof draft.schema_json === "string"
          ? draft.schema_json
          : JSON.stringify(draft.schema_json || {}, null, 2),
      modelConfig: {
        model: draft.model || "gpt-4o",
        temperature: draft.temperature ?? 0.2,
        max_tokens: draft.max_tokens ?? 4000,
      },
      description: draft.description || "",
      notes: draft.notes || "",
      jsonPrompt: options.jsonPromptValue ?? jsonPrompt,
      constraintsJson: options.constraintsJsonValue ?? constraintsJson,
      returnToFrontend: toBooleanFlag(draft.return_to_frontend),
      aiSchemaDraft: clonePlainValue(draft),
      aiConstraintMasterDraftRows: clonePlainValue(constraintDraftRows),
      aiSchemaChatMessages: clonePlainValue(chatMessages),
      aiSchemaTypeName: schemaTypeName,
      aiSchemaMode: options.aiSchemaModeValue || "edit",
      editorTab: options.editorTabValue ?? editorTab,
      selectedConstraintComponent:
        options.selectedConstraintComponentValue ?? schemaTypeName ?? "",
      selectedConstraintTarget: options.selectedConstraintTargetValue ?? "",
      aiConstraintsEnabled:
        options.aiConstraintsEnabledValue ?? aiConstraintsEnabled,
      selectedAiMappingTypes:
        options.selectedAiMappingTypesValue ?? selectedAiMappingTypes,
      selectedAiConstraintTypes:
        options.selectedAiConstraintTypesValue ?? selectedAiConstraintTypes,
    };
  };

  const openAiEditSchemaDialog = () => {
    if (!selectedSchemaType) return;

    const initialDraft = {
      system_prompt: systemPrompt || "",
      user_template: userPromptTemplate || "",
      model: modelConfig.model || "gpt-4o",
      temperature: modelConfig.temperature ?? 0.2,
      max_tokens: modelConfig.max_tokens ?? 4000,
      schema_json: outputSchema || "{}",
      description: description || "",
      notes: notes || "",
      return_to_frontend: returnToFrontend,
      constraints: { master_rows: [] },
    };
    const initialChatMessages = getInitialAiSchemaChatMessages();

    setAiSchemaMode("edit");
    setAiSchemaTypeName(selectedSchemaType);
    setAiSchemaDescription("");
    setAiSchemaDraft(initialDraft);
    setAiSchemaTab(0);
    setAiConstraintMasterDraftRows([]);
    setAiChangeScope(DEFAULT_AI_CHANGE_SCOPE);
    setAiConstraintsEnabled(false);
    setAiSchemaChatMessages(initialChatMessages);
    ensureOriginalRestorePoint(
      createRestoreSnapshotFromDraft(initialDraft, {
        schemaTypeName: selectedSchemaType,
        chatMessages: initialChatMessages,
        constraintDraftRows: [],
        aiConstraintsEnabledValue: false,
        selectedConstraintComponentValue: selectedSchemaType,
      }),
      selectedSchemaType,
    );
    setAiSchemaError(null);
    setActiveTab(0);
    setAiSchemaDialogOpen(false);
  };

  const normalizeAiConstraintMasterRows = (constraintsDraft, schemaTypeName) => {
    const rawRows = Array.isArray(constraintsDraft)
      ? constraintsDraft
      : Array.isArray(constraintsDraft?.master_rows)
        ? constraintsDraft.master_rows
        : [];

    const normalizedRows = rawRows
      .map((row, index) => {
        const targetField = String(row.target_field || row.targetField || row.target || "").trim();
        const sourceCategory = String(
          row.source_category || row.sourceCategory || row.category || "",
        ).trim();
        const componentName = String(row.component || schemaTypeName || selectedSchemaType || "").trim();

        if (!componentName || !targetField || !sourceCategory) return null;

        return {
          id: row.id || `draft-${Date.now()}-${index}`,
          __draft: true,
          tower: selectedTower,
          commodity: selectedCommodity,
          component: componentName,
          source_field: row.source_field || row.sourceField || "",
          source_category: sourceCategory,
          target_field: targetField,
          allowed_values: Array.isArray(row.allowed_values)
            ? row.allowed_values.join(";")
            : row.allowed_values || row.allowedValues || "",
          min_value: row.min_value ?? row.minValue ?? null,
          max_value: row.max_value ?? row.maxValue ?? null,
          guidance_text: row.guidance_text || row.guidanceText || "",
          is_active: row.is_active === undefined ? true : toBooleanFlag(row.is_active),
          field_description: row.field_description || row.fieldDescription || "",
          selection_guidance: row.selection_guidance || row.selectionGuidance || "",
          decision_inputs: row.decision_inputs || row.decisionInputs || "",
          fallback_guidance: row.fallback_guidance || row.fallbackGuidance || "",
          mapping_type: row.mapping_type || row.mappingType || inferMappingType(row),
          constraint_type: row.constraint_type || row.constraintType || inferConstraintType(row),
        };
      })
      .filter(Boolean);

    const draftRowsByKey = new Map();
    normalizedRows.forEach((row) => {
      const rowKey = getConstraintMasterRowKey(row);
      const existingRow = draftRowsByKey.get(rowKey);
      if (!existingRow) {
        draftRowsByKey.set(rowKey, {
          ...row,
          allowed_values: normalizeConstraintDelimitedValues(row.allowed_values),
        });
        return;
      }

      draftRowsByKey.set(rowKey, {
        ...existingRow,
        ...row,
        id: existingRow.id,
        __draft: true,
        min_value: row.min_value ?? existingRow.min_value,
        max_value: row.max_value ?? existingRow.max_value,
        mapping_type: row.mapping_type || existingRow.mapping_type,
        constraint_type: row.constraint_type || existingRow.constraint_type,
        allowed_values: normalizeConstraintDelimitedValues(
          [existingRow.allowed_values, row.allowed_values].filter(Boolean).join(";"),
        ),
        guidance_text: row.guidance_text || existingRow.guidance_text,
        field_description: row.field_description || existingRow.field_description,
        selection_guidance: row.selection_guidance || existingRow.selection_guidance,
        decision_inputs: row.decision_inputs || existingRow.decision_inputs,
        fallback_guidance: row.fallback_guidance || existingRow.fallback_guidance,
      });
    });

    return Array.from(draftRowsByKey.values());
  };

  const buildAiSchemaDraftFromResponse = (draft, fallbackDescription) => ({
    system_prompt: draft.system_prompt || "",
    user_template: draft.user_template || "",
    model: draft.model || "gpt-4o",
    temperature: draft.temperature ?? 0.2,
    max_tokens: draft.max_tokens ?? 4000,
    schema_json:
      typeof draft.schema_json === "string"
        ? draft.schema_json
        : JSON.stringify(draft.schema_json || {}, null, 2),
    description: draft.description || fallbackDescription,
    notes: draft.notes || "",
    return_to_frontend:
      draft.return_to_frontend === undefined
        ? selectedSchemaStage === "OutputSchema"
        : toBooleanFlag(draft.return_to_frontend),
    constraints: draft.constraints || { master_rows: [] },
  });

  const loadAiDraftFromSchema = (schemaType, schema) => {
    const initialDraft = {
      system_prompt: schema.system_prompt || "",
      user_template: schema.user_template || "",
      model: schema.model || "gpt-4o",
      temperature: schema.temperature ?? 0.2,
      max_tokens: schema.max_tokens ?? 4000,
      schema_json:
        typeof schema.schema_json === "string"
          ? schema.schema_json
          : JSON.stringify(schema.schema_json || {}, null, 2),
      description: schema.description || "",
      notes: schema.notes || "",
      return_to_frontend: toBooleanFlag(schema.return_to_frontend),
      constraints: { master_rows: [] },
    };
    const initialChatMessages = getInitialAiSchemaChatMessages();

    setAiSchemaMode("edit");
    setAiSchemaTypeName(schemaType);
    setAiSchemaDescription("");
    setAiSchemaDraft(initialDraft);
    setAiConstraintMasterDraftRows([]);
    setAiChangeScope(DEFAULT_AI_CHANGE_SCOPE);
    setAiConstraintsEnabled(false);
    setAiSchemaChatMessages(initialChatMessages);
    ensureOriginalRestorePoint(
      createRestoreSnapshotFromDraft(initialDraft, {
        schemaTypeName: schemaType,
        chatMessages: initialChatMessages,
        constraintDraftRows: [],
        jsonPromptValue: schema.image_prompt_json || "",
        constraintsJsonValue: schema.constraints_json || "",
        aiConstraintsEnabledValue: false,
        selectedConstraintComponentValue: schemaType,
      }),
      schemaType,
    );
    setAiSchemaError(null);
  };

  const getAiSchemaDraftForRequest = () => {
    if (!aiSchemaDraft) return null;

    let schemaJson = aiSchemaDraft.schema_json;
    try {
      schemaJson = JSON.parse(aiSchemaDraft.schema_json || "{}");
    } catch (error) {
      // Keep the current text so the AI can repair malformed JSON from a follow-up request.
    }

    return {
      ...aiSchemaDraft,
      schema_json: schemaJson,
      constraints: {
        ...(aiSchemaDraft.constraints || {}),
        master_rows: aiConstraintMasterDraftRows.map(({ __draft, id, ...row }) => row),
      },
    };
  };

  const getConstraintMasterContextForAi = () => ({
    selected_component: selectedConstraintComponent || "",
    selected_target_field: selectedConstraintTarget || "",
    component_count: constraintComponentGroups.length,
    target_count: constraintTargetGroups.length,
    component_groups: constraintComponentGroups.map((group) => ({
      component: group.component,
      active_count: group.activeCount,
      count: group.count,
    })),
    target_groups_for_selected_component: constraintTargetGroups.map((group) => ({
      target_field: group.targetField,
      active_count: group.activeCount,
      count: group.count,
    })),
    selected_component_active_count: selectedConstraintComponentActiveCount,
    selected_target_active_count: selectedConstraintTargetActiveCount,
    selected_rows: constraintMasterTableRows.slice(0, 50).map((row) => ({
      id: row.id,
      component: row.component,
      source_field: row.source_field,
      target_field: row.target_field,
      source_category: row.source_category,
      allowed_values: row.allowed_values,
      min_value: row.min_value,
      max_value: row.max_value,
      guidance_text: row.guidance_text,
      is_active: row.is_active,
      field_description: row.field_description,
      selection_guidance: row.selection_guidance,
      decision_inputs: row.decision_inputs,
      fallback_guidance: row.fallback_guidance,
      mapping_type: inferMappingType(row),
      constraint_type: inferConstraintType(row),
    })),
    selected_mapping_types: aiConstraintsRequested ? selectedAiMappingTypes : [],
    selected_constraint_types: aiConstraintsRequested ? selectedAiConstraintTypes : [],
    constraints_requested: aiConstraintsRequested,
    example_rows_for_selected_types: aiConstraintExampleRows.slice(0, 30).map((row) => ({
      id: row.id,
      component: row.component,
      source_field: row.source_field,
      source_category: row.source_category,
      target_field: row.target_field,
      allowed_values: row.allowed_values,
      min_value: row.min_value,
      max_value: row.max_value,
      guidance_text: row.guidance_text,
      is_active: row.is_active,
      field_description: row.field_description,
      selection_guidance: row.selection_guidance,
      decision_inputs: row.decision_inputs,
      fallback_guidance: row.fallback_guidance,
      mapping_type: inferMappingType(row),
      constraint_type: inferConstraintType(row),
      rule_preview: buildConstraintRulePreview(row),
    })),
    constraint_generation_rules: {
      Category:
        "Use allowed_values as a semicolon-separated allowed list; leave min_value and max_value null unless the row provides them.",
      Range:
        "Use min_value and max_value as numeric bounds; leave allowed_values blank unless the row provides it.",
      Guidance:
        "Use guidance_text, field_description, selection_guidance, decision_inputs, and fallback_guidance as guidance-only constraints.",
      Direct: "Apply when source_field in the user payload matches source_category.",
      Derived: "Apply when a derived categorization for component/target_field selects source_category.",
      DerivedContext: "Use derived context values for the target field and attach the guidance text as context.",
    },
  });

  const applyAiDraftToEditorState = (draft) => {
    if (!draft) return;

    setSystemPrompt(draft.system_prompt || "");
    setUserPromptTemplate(draft.user_template || "");
    setOutputSchema(
      typeof draft.schema_json === "string"
        ? draft.schema_json
        : JSON.stringify(draft.schema_json || {}, null, 2),
    );
    setModelConfig({
      model: draft.model || "gpt-4o",
      temperature: draft.temperature ?? 0.2,
      max_tokens: draft.max_tokens ?? 4000,
    });
    setDescription(draft.description || "");
    setNotes(draft.notes || "");
    setReturnToFrontend(toBooleanFlag(draft.return_to_frontend));
  };

  const getCurrentRestoreSnapshot = () => ({
    systemPrompt,
    userPromptTemplate,
    outputSchema,
    modelConfig: clonePlainValue(modelConfig),
    description,
    notes,
    jsonPrompt,
    constraintsJson,
    returnToFrontend,
    aiSchemaDraft: aiSchemaDraft ? clonePlainValue(aiSchemaDraft) : null,
    aiConstraintMasterDraftRows: clonePlainValue(aiConstraintMasterDraftRows || []),
    aiSchemaChatMessages: clonePlainValue(aiSchemaChatMessages || []),
    aiSchemaTypeName,
    aiSchemaMode,
    editorTab,
    selectedConstraintComponent,
    selectedConstraintTarget,
    aiConstraintsEnabled,
    selectedAiMappingTypes,
    selectedAiConstraintTypes,
  });

  const applyRestoreSnapshotToEditor = (snapshot, options = {}) => {
    if (!snapshot) return;

    const { preserveChat = true } = options;

    setSystemPrompt(snapshot.systemPrompt || "");
    setUserPromptTemplate(snapshot.userPromptTemplate || "");
    setOutputSchema(snapshot.outputSchema || "{}");
    setModelConfig(snapshot.modelConfig || { model: "gpt-4o", temperature: 0.2, max_tokens: 4000 });
    setDescription(snapshot.description || "");
    setNotes(snapshot.notes || "");
    setJsonPrompt(snapshot.jsonPrompt || "");
    setConstraintsJson(snapshot.constraintsJson || "");
    setReturnToFrontend(toBooleanFlag(snapshot.returnToFrontend));
    setAiSchemaDraft(snapshot.aiSchemaDraft ? clonePlainValue(snapshot.aiSchemaDraft) : null);
    setAiConstraintMasterDraftRows(
      (snapshot.aiConstraintMasterDraftRows || []).map((row) => ({ ...row, __draft: true })),
    );
    if (!preserveChat) {
      setAiSchemaChatMessages(snapshot.aiSchemaChatMessages || []);
    }
    setAiSchemaTypeName(snapshot.aiSchemaTypeName || selectedSchemaType || "");
    setAiSchemaMode(snapshot.aiSchemaMode || "edit");
    // Keep the user's current editor tab when loading a restore point.
    setSelectedConstraintComponent(snapshot.selectedConstraintComponent || selectedSchemaType || "");
    setSelectedConstraintTarget(snapshot.selectedConstraintTarget || "");
    setAiConstraintsEnabled(
      snapshot.aiConstraintsEnabled ??
        (snapshot.selectedAiMappingTypes?.length > 0 &&
          snapshot.selectedAiConstraintTypes?.length > 0),
    );
    setSelectedAiMappingTypes(snapshot.selectedAiMappingTypes || []);
    setSelectedAiConstraintTypes(snapshot.selectedAiConstraintTypes || []);
  };

  const createRestorePointId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const ensureOriginalRestorePoint = (snapshot, schemaTypeName) => {
    const resolvedSchemaType = schemaTypeName || selectedSchemaType || aiSchemaTypeName || "";
    if (!resolvedSchemaType || !snapshot) return null;

    const key = buildRestorePointKey(
      selectedTower,
      selectedCommodity,
      selectedSchemaStage,
      resolvedSchemaType,
    );
    const restorePoint = {
      id: `original-${key}`,
      createdAt: new Date().toISOString(),
      schema_type: resolvedSchemaType,
      instruction: "Original schema",
      label: "Original schema",
      phase: "original",
      change_scope: DEFAULT_AI_CHANGE_SCOPE,
      snapshot: clonePlainValue(snapshot),
    };

    setSchemaRestorePoints((previous) => {
      const existingPoints = previous[key] || [];
      if (existingPoints.some((point) => point.phase === "original")) {
        return previous;
      }

      return {
        ...previous,
        [key]: [...existingPoints, restorePoint],
      };
    });

    return restorePoint;
  };

  const addRestorePoint = (restorePoint, options = {}) => {
    const { select = true } = options;

    setSchemaRestorePoints((previous) => ({
      ...previous,
      [restorePointKey]: [restorePoint, ...(previous[restorePointKey] || [])],
    }));
    if (select) {
      setSelectedRestorePointId(restorePoint.id);
    }
    return restorePoint;
  };

  const createRestorePointAfterAiResult = ({
    id,
    instruction,
    nextDraft,
    nextConstraintDraftRows,
    nextChatMessages,
    nextEditorTab,
    nextSelectedConstraintComponent,
    nextSelectedConstraintTarget,
  }) => {
    if (!restorePointKey.trim()) return null;

    const createdAt = new Date().toISOString();
    const restorePoint = {
      id: id || createRestorePointId(),
      createdAt,
      schema_type: selectedSchemaType || aiSchemaTypeName || "",
      instruction,
      label: `Result: ${instruction.length > 72 ? `${instruction.slice(0, 72)}...` : instruction}`,
      phase: "after",
      change_scope: clonePlainValue(aiChangeScope),
      snapshot: {
        systemPrompt: nextDraft.system_prompt || "",
        userPromptTemplate: nextDraft.user_template || "",
        outputSchema:
          typeof nextDraft.schema_json === "string"
            ? nextDraft.schema_json
            : JSON.stringify(nextDraft.schema_json || {}, null, 2),
        modelConfig: {
          model: nextDraft.model || "gpt-4o",
          temperature: nextDraft.temperature ?? 0.2,
          max_tokens: nextDraft.max_tokens ?? 4000,
        },
        description: nextDraft.description || "",
        notes: nextDraft.notes || "",
        jsonPrompt,
        constraintsJson,
        returnToFrontend: toBooleanFlag(nextDraft.return_to_frontend),
        aiSchemaDraft: clonePlainValue(nextDraft),
        aiConstraintMasterDraftRows: clonePlainValue(nextConstraintDraftRows || []),
        aiSchemaChatMessages: clonePlainValue(nextChatMessages || []),
        aiSchemaTypeName: selectedSchemaType || aiSchemaTypeName || "",
        aiSchemaMode,
        editorTab: nextEditorTab,
        selectedConstraintComponent: nextSelectedConstraintComponent || "",
        selectedConstraintTarget: nextSelectedConstraintTarget || "",
        aiConstraintsEnabled,
        selectedAiMappingTypes,
        selectedAiConstraintTypes,
      },
    };

    return addRestorePoint(restorePoint);
  };

  const handleRestorePointSelect = (restorePoint) => {
    setSelectedRestorePointId(restorePoint.id);
    applyRestoreSnapshotToEditor(restorePoint.snapshot, { preserveChat: true });
    setSuccess("Restore point applied. Click Save to persist this instance.");
  };

  const getRestorePointForMessage = (message) => {
    if (!message) return null;
    if (message.role !== "assistant") return null;
    if (message.restorePointId) {
      const byId = currentRestorePoints.find((point) => point.id === message.restorePointId);
      if (byId) return byId;
    }
    return null;
  };

  const getRestorePointNumber = (restorePoint) => {
    if (!restorePoint || restorePoint.phase === "original") return "";
    const resultRestorePoints = currentRestorePoints
      .filter((point) => point.phase === "after")
      .slice()
      .reverse();
    const restoreIndex = resultRestorePoints.findIndex((point) => point.id === restorePoint.id);
    return restoreIndex >= 0 ? restoreIndex + 1 : "";
  };

  const renderRestorePointMarker = (restorePoint) => {
    if (!restorePoint) return null;
    const restorePointNumber = getRestorePointNumber(restorePoint);
    const restorePointIsSelected = selectedRestorePointId === restorePoint.id;
    const restorePointLabel =
      restorePoint.phase === "original"
        ? "Original"
        : restorePointNumber
          ? `RP ${restorePointNumber}`
          : "Restore";
    const restorePointTooltip =
      restorePoint.phase === "original" ? "Load original state" : "Load AI result";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          alignSelf: "stretch",
          my: 0.15,
        }}
      >
        <Divider sx={{ flex: 1 }} />
        <Tooltip title={restorePointTooltip}>
          <Box
            component="button"
            type="button"
            onClick={() => handleRestorePointSelect(restorePoint)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.35,
              border: 1,
              borderColor: restorePointIsSelected ? "primary.main" : "divider",
              bgcolor: restorePointIsSelected ? "action.selected" : "background.paper",
              color: restorePointIsSelected ? "primary.main" : "text.secondary",
              borderRadius: 999,
              px: 0.75,
              py: 0.1,
              font: "inherit",
              cursor: "pointer",
            }}
          >
            <HistoryIcon sx={{ fontSize: 13 }} />
            <Typography variant="caption" sx={{ fontSize: "0.66rem" }}>
              {restorePointLabel}
            </Typography>
          </Box>
        </Tooltip>
        <Divider sx={{ flex: 1 }} />
      </Box>
    );
  };

  const handleAiChangeScopeToggle = (scopeValue) => {
    setAiChangeScope((previousScope) =>
      previousScope.includes(scopeValue)
        ? previousScope.filter((value) => value !== scopeValue)
        : [...previousScope, scopeValue],
    );
  };

  const handleAiChangeScopeAllToggle = () => {
    setAiChangeScope((previousScope) =>
      DEFAULT_AI_CHANGE_SCOPE.every((scopeValue) => previousScope.includes(scopeValue))
        ? []
        : DEFAULT_AI_CHANGE_SCOPE,
    );
  };

  const toggleListValue = (setter, optionValue) => {
    setter((previousValues) =>
      previousValues.includes(optionValue)
        ? previousValues.filter((value) => value !== optionValue)
        : [...previousValues, optionValue],
    );
  };

  const handleAiMappingTypeToggle = (mappingType) => {
    toggleListValue(setSelectedAiMappingTypes, mappingType);
  };

  const handleAiConstraintTypeToggle = (constraintType) => {
    toggleListValue(setSelectedAiConstraintTypes, constraintType);
  };

  const allAiChangeScopesSelected = DEFAULT_AI_CHANGE_SCOPE.every((scopeValue) =>
    aiChangeScope.includes(scopeValue),
  );
  const handleGenerateAiSchemaDraft = async () => {
    const schemaTypeName = aiSchemaTypeName.trim();
    const schemaInstruction = aiSchemaDescription.trim();
    const isRevision = Boolean(aiSchemaDraft);
    const effectiveChangeScope = [
      ...(isRevision ? aiChangeScope : DEFAULT_AI_CHANGE_SCOPE),
      ...(aiConstraintsRequested ? [AI_CONSTRAINT_SCOPE_VALUE] : []),
    ].filter((value, index, list) => list.indexOf(value) === index);

    if (!schemaTypeName || !schemaInstruction || (isRevision && effectiveChangeScope.length === 0)) {
      return;
    }

    try {
      setAiSchemaGenerating(true);
      setAiSchemaError(null);
      setError(null);
      setSuccess(null);
      const nextChatMessages = [
        ...aiSchemaChatMessages,
        { role: "user", content: schemaInstruction },
      ];
      setAiSchemaChatMessages(nextChatMessages);

      const response = await apiFetch(
        `${API_BASE}/schema-v3/${isRevision ? "refine-draft" : "generate-draft"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tower: selectedTower,
            commodity: selectedCommodity,
            schema_stage: selectedSchemaStage,
            schema_type: schemaTypeName,
            description: schemaInstruction,
            instruction: schemaInstruction,
            draft: getAiSchemaDraftForRequest(),
            chat_history: nextChatMessages,
            change_scope: effectiveChangeScope,
            constraint_master_context: getConstraintMasterContextForAi(),
          }),
        },
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to generate schema draft");
      }

      const draft = data.draft || {};
      const nextDraft = buildAiSchemaDraftFromResponse(draft, schemaInstruction);
      const generatedConstraintDraftRows = normalizeAiConstraintMasterRows(
        nextDraft.constraints,
        schemaTypeName,
      );
      const nextConstraintDraftRows = aiConstraintsRequested
        ? generatedConstraintDraftRows
        : aiConstraintMasterDraftRows;
      const nextEditorTab = aiConstraintsRequested ? 3 : editorTab;
      const nextSelectedConstraintComponent =
        nextConstraintDraftRows[0]?.component || selectedConstraintComponent;
      const nextSelectedConstraintTarget =
        nextConstraintDraftRows[0]?.target_field || selectedConstraintTarget;
      setAiSchemaDraft(nextDraft);
      applyAiDraftToEditorState(nextDraft);
      if (aiConstraintsRequested) {
        setAiConstraintMasterDraftRows(nextConstraintDraftRows);
        setEditorTab(nextEditorTab);
        if (nextConstraintDraftRows.length > 0) {
          setSelectedConstraintComponent(nextSelectedConstraintComponent);
          setSelectedConstraintTarget(nextSelectedConstraintTarget);
        }
      }
      setAiSchemaDescription("");
      const resultRestorePointId = createRestorePointId();
      const assistantMessage = {
        role: "assistant",
        restorePointId: resultRestorePointId,
        restorePointPosition: "after",
        content: buildAiActionSummary({
          isRevision,
          effectiveChangeScope,
          schemaJsonChanged: data.schemaJsonChanged,
          nextDraft,
          nextConstraintDraftRows,
          aiConstraintsRequested,
          selectedAiMappingTypes,
          selectedAiConstraintTypes,
        }),
      };
      const finalChatMessages = [...nextChatMessages, assistantMessage];
      createRestorePointAfterAiResult({
        id: resultRestorePointId,
        instruction: schemaInstruction,
        nextDraft,
        nextConstraintDraftRows,
        nextChatMessages: finalChatMessages,
        nextEditorTab,
        nextSelectedConstraintComponent,
        nextSelectedConstraintTarget,
      });
      setAiSchemaChatMessages(finalChatMessages);
    } catch (error) {
      setAiSchemaError(error.message || "Failed to generate schema draft");
    } finally {
      setAiSchemaGenerating(false);
    }
  };

  const handleCreateAiSchemaType = async () => {
    const schemaTypeName = aiSchemaTypeName.trim();
    if (!schemaTypeName || !aiSchemaDraft) return;

    let schemaJson;
    try {
      schemaJson = JSON.parse(aiSchemaDraft.schema_json || "{}");
    } catch (error) {
      setAiSchemaError("Invalid JSON in Output Schema");
      return;
    }

    try {
      setSchemaTypeActionLoading(true);
      setAiSchemaError(null);
      setError(null);
      setSuccess(null);

      const draftTemperature = parseFloat(aiSchemaDraft.temperature);
      const draftMaxTokens = parseInt(aiSchemaDraft.max_tokens);

      const response = await apiFetch(`${API_BASE}/schema-v3/create-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_stage: selectedSchemaStage,
          schema_type: schemaTypeName,
          schema_json: schemaJson,
          system_prompt: aiSchemaDraft.system_prompt,
          user_template: aiSchemaDraft.user_template,
          model: aiSchemaDraft.model || "gpt-4o",
          temperature: Number.isFinite(draftTemperature) ? draftTemperature : 0.2,
          max_tokens: Number.isFinite(draftMaxTokens) ? draftMaxTokens : null,
          description: aiSchemaDraft.description || aiSchemaDescription,
          notes: aiSchemaDraft.notes,
          return_to_frontend: aiSchemaDraft.return_to_frontend,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to create schema type");
      }

      setAiSchemaDialogOpen(false);
      await fetchSchemaTypes();
      await loadSchemaType(schemaTypeName);
      setSuccess(`Schema '${schemaTypeName}' created successfully`);
    } catch (error) {
      setAiSchemaError(error.message || "Failed to create schema type");
    } finally {
      setSchemaTypeActionLoading(false);
    }
  };

  const handleApplyAiSchemaDraftToEditor = () => {
    if (!aiSchemaDraft) return;

    let schemaJson;
    try {
      schemaJson = JSON.parse(aiSchemaDraft.schema_json || "{}");
    } catch (error) {
      setAiSchemaError("Invalid JSON in Output Schema");
      return;
    }

    const draftTemperature = parseFloat(aiSchemaDraft.temperature);
    const draftMaxTokens = parseInt(aiSchemaDraft.max_tokens);

    applyAiDraftToEditorState({
      ...aiSchemaDraft,
      schema_json: schemaJson,
      temperature: Number.isFinite(draftTemperature) ? draftTemperature : 0.2,
      max_tokens: Number.isFinite(draftMaxTokens) ? draftMaxTokens : null,
    });
    setAiSchemaDialogOpen(false);
    setSuccess("AI changes applied to the editor. Click Save to update the current schema.");
  };

  const openDuplicateSchemaDialog = () => {
    if (schemaTypes.length === 0) return;
    setDuplicateSourceSchemaType(selectedSchemaType || schemaTypes[0]?.schema_type || "");
    setDuplicateTargetSchemaType("");
    setDuplicateSchemaDescription("");
    setDuplicateSchemaDialogOpen(true);
  };

  const openRenameSchemaDialog = () => {
    if (!selectedSchemaType) return;
    setRenameSchemaTypeName(selectedSchemaType);
    setRenameSchemaDialogOpen(true);
  };

  const handleRenameSchemaType = async () => {
    const targetSchemaType = renameSchemaTypeName.trim();
    if (!selectedSchemaType || !targetSchemaType) return;

    try {
      setSchemaTypeActionLoading(true);
      setError(null);
      setSuccess(null);

      const response = await apiFetch(`${API_BASE}/schema-v3/rename-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_stage: selectedSchemaStage,
          source_schema_type: selectedSchemaType,
          target_schema_type: targetSchemaType,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to rename schema type");
      }

      setRenameSchemaDialogOpen(false);
      setSchemaCache((prev) => {
        const next = { ...prev };
        if (next[selectedSchemaType]) {
          next[targetSchemaType] = next[selectedSchemaType];
          delete next[selectedSchemaType];
        }
        return next;
      });
      await fetchSchemaTypes();
      await loadSchemaType(targetSchemaType);
      setSuccess(`Schema renamed to '${targetSchemaType}'`);
    } catch (error) {
      setError(error.message || "Failed to rename schema type");
    } finally {
      setSchemaTypeActionLoading(false);
    }
  };

  const handleCreateSchemaType = async () => {
    const schemaTypeName = newSchemaTypeName.trim();
    if (!schemaTypeName) return;

    try {
      setSchemaTypeActionLoading(true);
      setError(null);
      setSuccess(null);

      const response = await apiFetch(`${API_BASE}/schema-v3/create-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_stage: selectedSchemaStage,
          schema_type: schemaTypeName,
          description: newSchemaTypeDescription,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to create schema type");
      }

      setCreateSchemaDialogOpen(false);
      await fetchSchemaTypes();
      await loadSchemaType(schemaTypeName);
      setSuccess(`Schema '${schemaTypeName}' created successfully`);
    } catch (error) {
      setError(error.message || "Failed to create schema type");
    } finally {
      setSchemaTypeActionLoading(false);
    }
  };

  const handleDuplicateSchemaType = async () => {
    const sourceSchemaType = duplicateSourceSchemaType.trim();
    const targetSchemaType = duplicateTargetSchemaType.trim();

    if (!sourceSchemaType || !targetSchemaType) return;

    try {
      setSchemaTypeActionLoading(true);
      setError(null);
      setSuccess(null);

      const response = await apiFetch(`${API_BASE}/schema-v3/duplicate-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_stage: selectedSchemaStage,
          source_schema_type: sourceSchemaType,
          target_schema_type: targetSchemaType,
          description: duplicateSchemaDescription,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to duplicate schema type");
      }

      setDuplicateSchemaDialogOpen(false);
      await fetchSchemaTypes();
      await loadSchemaType(targetSchemaType);
      setSuccess(`Schema '${sourceSchemaType}' duplicated to '${targetSchemaType}'`);
    } catch (error) {
      setError(error.message || "Failed to duplicate schema type");
    } finally {
      setSchemaTypeActionLoading(false);
    }
  };

  const loadConstraintRules = async (schemaType) => {
    setLoadingConstraintRules(true);
    console.log(
      `🔍 Loading constraint rules for: tower=${selectedTower}, commodity=${selectedCommodity}, schema_type=${schemaType}`,
    );
    try {
      const url = `${API_BASE}/constraint-rules?tower=${selectedTower}&commodity=${selectedCommodity}&schema_type=${schemaType}`;
      console.log(`📡 Fetching from: ${url}`);
      const response = await apiFetch(url);
      const data = await response.json();
      console.log(`📦 Received data:`, data);
      const rules = data.rules || [];
      console.log(`✅ Loaded ${rules.length} constraint rules`);
      setConstraintRules(rules);
      // Auto-select first rule if available
      if (rules.length > 0 && !selectedConstraintRule) {
        setSelectedConstraintRule(rules[0]);
        console.log(`🎯 Auto-selected rule:`, rules[0]);
      }
    } catch (error) {
      console.error("❌ Failed to load constraint rules:", error);
      setConstraintRules([]);
    } finally {
      setLoadingConstraintRules(false);
    }
  };

  const saveRuleEdit = async () => {
    try {
      setError(null);
      setSuccess(null);

      const payload = {
        priority: editedRuleData.priority,
        is_active: editedRuleData.active,
        condition_json: editedRuleData.condition_json,
      };

      console.log(`💾 Saving rule ${selectedConstraintRule.rule_id}:`, payload);

      const response = await apiFetch(
        `${API_BASE}/constraint-rules/${selectedConstraintRule.rule_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setSuccess("Rule updated successfully");
        setEditingRule(false);
        setEditedRuleData(null);
        // Reload rules to get updated data
        await loadConstraintRules(selectedSchemaType);
        // Update selected rule
        const updatedRule = constraintRules.find(
          (r) => r.rule_id === selectedConstraintRule.rule_id,
        );
        if (updatedRule) {
          setSelectedConstraintRule(updatedRule);
        }
      } else {
        setError(data.error || "Failed to update rule");
      }
    } catch (error) {
      console.error("❌ Error saving rule:", error);
      setError(`Failed to save rule: ${error.message}`);
    }
  };

  const saveConstraintSection = async (sectionKey) => {
    try {
      setError(null);
      setSuccess(null);

      const payload = {
        section_key: sectionKey,
        section_value: editedConstraints[sectionKey],
      };

      console.log(
        `💾 Saving constraint section ${sectionKey} for rule ${selectedConstraintRule.rule_id}:`,
        payload,
      );

      const response = await apiFetch(
        `${API_BASE}/constraint-rules/${selectedConstraintRule.rule_id}/constraints`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setSuccess(`Section ${sectionKey} updated successfully`);
        setEditingSection(null);
        setEditedConstraints({});
        // Reload rules to get updated data
        await loadConstraintRules(selectedSchemaType);
        // Update selected rule
        const updatedRule = constraintRules.find(
          (r) => r.rule_id === selectedConstraintRule.rule_id,
        );
        if (updatedRule) {
          setSelectedConstraintRule(updatedRule);
        }
      } else {
        setError(data.error || "Failed to update constraint section");
      }
    } catch (error) {
      console.error("❌ Error saving constraint section:", error);
      setError(`Failed to save section: ${error.message}`);
    }
  };

  const createNewRule = async () => {
    try {
      setError(null);
      setSuccess(null);

      const payload = {
        tower: selectedTower,
        commodity: selectedCommodity,
        schema_type: selectedSchemaType,
        priority: newRuleData.priority,
        is_active: newRuleData.active,
        condition_json: newRuleData.condition_json,
        constraints_json: newRuleData.constraints_json,
        notes: newRuleData.notes,
      };

      console.log(`💾 Creating new rule:`, payload);

      const response = await apiFetch(`${API_BASE}/constraint-rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === "success") {
        setSuccess("Rule created successfully");
        setNewRuleDialogOpen(false);
        setNewRuleData({
          priority: 100,
          active: true,
          condition_json: {},
          constraints_json: {},
          notes: "",
        });
        // Reload rules
        await loadConstraintRules(selectedSchemaType);
      } else {
        setError(data.error || "Failed to create rule");
      }
    } catch (error) {
      console.error("❌ Error creating rule:", error);
      setError(`Failed to create rule: ${error.message}`);
    }
  };

  const handleUpdateSchema = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Parse output schema to validate JSON
      let schemaJson;
      try {
        schemaJson = JSON.parse(outputSchema);
      } catch (e) {
        throw new Error("Invalid JSON in Output Schema");
      }

      const payload = {
        tower: selectedTower,
        commodity: selectedCommodity,
        schema_stage: selectedSchemaStage,
        schema_type: selectedSchemaType,
        schema_json: schemaJson,
        system_prompt: systemPrompt,
        user_template: userPromptTemplate,
        model: modelConfig.model,
        temperature: parseFloat(modelConfig.temperature),
        max_tokens: modelConfig.max_tokens ? parseInt(modelConfig.max_tokens) : null,
        description: description,
        notes: notes,
        image_prompt_json: jsonPrompt,
        constraints_json: constraintsJson,
        return_to_frontend: returnToFrontend,
      };

      const response = await apiFetch(`${API_BASE}/schema-v3/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        const savedConstraintDraftCount = await saveAiConstraintMasterDraftRows();
        setSuccess(
          savedConstraintDraftCount > 0
            ? `Schema updated and ${savedConstraintDraftCount} AI constraint draft row${
                savedConstraintDraftCount === 1 ? "" : "s"
              } saved`
            : "Schema updated successfully",
        );
        // Update cache with new data
        setSchemaCache((prev) => ({
          ...prev,
          [selectedSchemaType]: {
            system_prompt: systemPrompt,
            user_template: userPromptTemplate,
            schema_json: schemaJson,
            model: modelConfig.model,
            temperature: modelConfig.temperature,
            max_tokens: modelConfig.max_tokens,
            description: description,
            notes: notes,
            image_prompt_json: jsonPrompt,
            constraints_json: constraintsJson,
            return_to_frontend: returnToFrontend,
          },
        }));
        loadAuditLog(selectedSchemaType);
      } else {
        throw new Error(data.error || "Failed to update schema");
      }
    } catch (error) {
      setError(`Update failed: ${getApiErrorMessage(error, "Failed to update schema")}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchema = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Parse output schema to validate JSON
      let schemaJson;
      try {
        schemaJson = JSON.parse(outputSchema);
      } catch (e) {
        throw new Error("Invalid JSON in Output Schema");
      }

      const payload = {
        tower: selectedTower,
        commodity: selectedCommodity,
        schema_stage: selectedSchemaStage,
        schema_type: selectedSchemaType,
        schema_version: parseInt(newVersion) || 1,
        schema_json: schemaJson,
        system_prompt: systemPrompt,
        user_template: userPromptTemplate,
        model: modelConfig.model,
        temperature: parseFloat(modelConfig.temperature),
        max_tokens: modelConfig.max_tokens ? parseInt(modelConfig.max_tokens) : null,
        description: versionDescription || description,
        notes: notes,
        is_active: false, // New versions are inactive by default
        image_prompt_json: jsonPrompt,
        constraints_json: constraintsJson,
        return_to_frontend: returnToFrontend,
      };

      const response = await apiFetch(`${API_BASE}/schema-v3/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        const savedConstraintDraftCount = await saveAiConstraintMasterDraftRows();
        setSuccess(
          savedConstraintDraftCount > 0
            ? `Schema saved as version ${newVersion} and ${savedConstraintDraftCount} AI constraint draft row${
                savedConstraintDraftCount === 1 ? "" : "s"
              } saved`
            : `Schema saved successfully as version ${newVersion}`,
        );
        setSaveDialogOpen(false);
        setNewVersion("");
        setVersionDescription("");
        loadVersions(selectedSchemaType);
        loadAuditLog(selectedSchemaType);
      } else {
        throw new Error(data.error || "Failed to save schema");
      }
    } catch (error) {
      setError(`Save failed: ${getApiErrorMessage(error, "Failed to save schema")}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRefinePrompt = async () => {
    setRefining(true);
    try {
      if (!selectedVariant) {
        setError("Please select a variant to apply");
        setRefining(false);
        return;
      }

      // Apply the selected variant directly
      if (refineType === "system") {
        setSystemPrompt(selectedVariant);
      } else {
        setUserPromptTemplate(selectedVariant);
      }

      setSuccess("Prompt variant applied successfully");
      setRefineDialogOpen(false);
      setAiVariants([]);
      setSelectedVariant(null);
    } catch (error) {
      setError(`Apply failed: ${error.message}`);
    } finally {
      setRefining(false);
    }
  };

  const loadAiVariants = async (promptText, type) => {
    setLoadingVariants(true);
    try {
      const response = await apiFetch(`${API_BASE}/suggest-refinements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: promptText,
          promptType: type,
          schemaType: selectedSchemaType,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAiVariants(data.suggestions || []);
      } else {
        throw new Error(data.error || "Failed to load variants");
      }
    } catch (error) {
      setError(`Failed to load variants: ${error.message}`);
      setAiVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  const openRefineDialog = (type) => {
    const promptText = type === "system" ? systemPrompt : userPromptTemplate;
    setRefineType(type);
    setCurrentPromptText(promptText);
    setRefineDialogOpen(true);
    setAiVariants([]);
    setSelectedVariant(null);

    // Load AI variants automatically
    loadAiVariants(promptText, type);
  };

  const handleActivateVersion = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/schema-v3/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_stage: selectedSchemaStage,
          schema_type: selectedSchemaType,
          schema_version: versionToActivate,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Version ${versionToActivate} activated successfully`);
        setActivateDialogOpen(false);
        setVersionToActivate(null);
        loadVersions(selectedSchemaType);
        loadAuditLog(selectedSchemaType);
        loadSchemaType(selectedSchemaType); // Reload to show active version
      } else {
        throw new Error(data.error || "Failed to activate version");
      }
    } catch (error) {
      setError(`Activation failed: ${error.message}`);
    }
  };

  const openSaveDialog = () => {
    // Auto-increment version
    const maxVersion = versions.reduce((max, v) => Math.max(max, v.schema_version), 0);
    setNewVersion((maxVersion + 1).toString());
    setSaveDialogOpen(true);
  };

  if (!isSuperAdmin) {
    return (
      <SidebarLayout>
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">This page is available to Super Admin only.</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  const hasSchemaWorkspace = Boolean(selectedSchemaType);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          height: "100%",
          bgcolor: "background.default",
          overflow: "hidden",
        }}
      >
        <Paper
          sx={{
            px: 2,
            py: 0.75,
            borderRadius: 0,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, flexShrink: 0 }}>
              Schema Definitions V3
            </Typography>
            <Divider orientation="vertical" flexItem />
            {selectedTower && selectedCommodity && (
              <Box
                component="img"
                src={`/images/Commodities/${
                  selectedCommodity.charAt(0).toUpperCase() +
                  selectedCommodity.slice(1).toLowerCase()
                }.jpg`}
                alt={selectedCommodity}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1,
                  objectFit: "cover",
                  border: 1,
                  borderColor: "divider",
                  flexShrink: 0,
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "0.95rem" }} noWrap>
              {selectedTower && selectedCommodity
                ? `${selectedTower}/${selectedCommodity}`
                : "Select a Schema"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Refresh Schema">
              <span>
                <IconButton
                  size="small"
                  onClick={() => selectedSchemaType && loadSchemaType(selectedSchemaType)}
                  disabled={!selectedSchemaType}
                  sx={{
                    "&:disabled": {
                      bgcolor: "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Save (Update Current Version)">
              <span>
                <IconButton
                  size="small"
                  onClick={handleUpdateSchema}
                  disabled={!selectedSchemaType || saving}
                  sx={{
                    "&:disabled": {
                      bgcolor: "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Save New Version">
              <span>
                <IconButton
                  size="small"
                  onClick={openSaveDialog}
                  disabled={!selectedSchemaType || saving}
                  sx={{
                    "&:disabled": {
                      bgcolor: "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", overflow: "hidden" }}>
        {/* Left Navigation - Schema Types */}
        <Paper
          sx={{
            width: schemaNavCollapsed ? 48 : 240,
            flexShrink: 0,
            borderRadius: 0,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "width 180ms ease",
          }}
        >
          {schemaNavCollapsed ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                py: 1,
              }}
            >
              <Tooltip title="Expand schema panel">
                <IconButton size="small" onClick={() => setSchemaNavCollapsed(false)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider flexItem />
              <Tooltip title="Schema Management">
                <Typography
                  variant="caption"
                  sx={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    color: "text.secondary",
                    fontWeight: 700,
                    letterSpacing: 1,
                    mt: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  SCHEMAS
                </Typography>
              </Tooltip>
            </Box>
          ) : (
            <>
          {/* Tower & Commodity Selection */}
          <Box sx={{ p: 1.25, borderBottom: 1, borderColor: "divider" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              <Tooltip title="Collapse schema panel">
                <IconButton size="small" onClick={() => setSchemaNavCollapsed(true)}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
              <InputLabel>Tower</InputLabel>
              <Select
                value={selectedTower}
                onChange={(e) => {
                  setSelectedTower(e.target.value);
                  setSelectedCommodity("");
                  setSchemaTypes([]);
                  setSelectedSchemaType(null);
                }}
                label="Tower"
              >
                {towers.map((tower) => (
                  <MenuItem key={tower.tower_code} value={tower.tower_code}>
                    {tower.tower_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!selectedTower}>
              <InputLabel>Commodity</InputLabel>
              <Select
                value={selectedCommodity}
                onChange={(e) => {
                  setSelectedCommodity(e.target.value);
                  setSelectedSchemaType(null);
                }}
                label="Commodity"
              >
                {commodities.map((commodity) => (
                  <MenuItem key={commodity.commodity_code} value={commodity.commodity_code}>
                    {commodity.commodity_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl component="fieldset" size="small" sx={{ mt: 1.25 }} disabled={!selectedCommodity}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                Schema Stage
              </Typography>
              <RadioGroup
                value={selectedSchemaStage}
                onChange={(e) => {
                  setSelectedSchemaStage(e.target.value);
                  setSelectedSchemaType(null);
                }}
              >
                <FormControlLabel
                  value="Constraints"
                  control={<Radio size="small" />}
                  label="Constraints"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
                />
                <FormControlLabel
                  value="OutputSchema"
                  control={<Radio size="small" />}
                  label="OutputSchema"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Schema Types List Header with Actions */}
          {selectedCommodity && (
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                borderTop: 1,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                SCHEMAS ({schemaTypes.length})
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="Add Schema with AI">
                  <span>
                    <IconButton
                      size="small"
                      onClick={openCreateSchemaDialog}
                      disabled={!selectedTower || !selectedCommodity || schemaTypeActionLoading}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Duplicate Existing Schema Type">
                  <span>
                    <IconButton
                      size="small"
                      onClick={openDuplicateSchemaDialog}
                      disabled={
                        !selectedTower ||
                        !selectedCommodity ||
                        schemaTypes.length === 0 ||
                        schemaTypeActionLoading
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Manage Groups">
                  <span>
                    <IconButton
                      size="small"
                      onClick={openExecutionGroupDialog}
                      disabled={schemaTypes.length === 0}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Schema Types List */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {!loading && schemaTypes.length === 0 && selectedCommodity && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No schemas found
                </Typography>
              </Box>
            )}

            <List sx={{ p: 0 }}>
              {schemaTypes.map((schemaType) => (
                <ListItem key={schemaType.schema_type} disablePadding>
                  <ListItemButton
                    selected={selectedSchemaType === schemaType.schema_type}
                    onClick={() => loadSchemaType(schemaType.schema_type)}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      setSelectedSchemaType(schemaType.schema_type);
                      setRenameSchemaTypeName(schemaType.schema_type);
                      setRenameSchemaDialogOpen(true);
                    }}
                    sx={{
                      py: 0.75,
                      px: 1.25,
                      borderLeft: 3,
                      borderColor:
                        selectedSchemaType === schemaType.schema_type
                          ? "primary.main"
                          : "transparent",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: selectedSchemaType === schemaType.schema_type ? 600 : 400,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {schemaType.schema_type}
                          </Typography>
                          <Chip
                            label={`${schemaType.execution_group || "?"}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              bgcolor:
                                selectedSchemaType === schemaType.schema_type
                                  ? "primary.main"
                                  : "action.selected",
                              color:
                                selectedSchemaType === schemaType.schema_type
                                  ? "primary.contrastText"
                                  : "text.secondary",
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
            </>
          )}
        </Paper>

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Content Area */}
          {loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={60} />
            </Box>
          ) : hasSchemaWorkspace ? (
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", overflow: "hidden" }}>
              {/* Left Panel - Editor */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  borderRight: 1,
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={editorTab}
                  onChange={(e, v) => {
                    setEditorTab(v);
                    if (v === 3 && selectedTower && selectedCommodity) {
                      fetchConstraintMasterItems(selectedTower, selectedCommodity);
                    }
                  }}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="System Prompt" />
                  <Tab label="User Prompt" />
                  <Tab label="Output Schema" />
                  <Tab label="Constraints" />
                  <Tab label="Model Config" />
                </Tabs>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: editorTab === 3 ? "hidden" : "auto",
                    p: 2,
                    bgcolor: "background.default",
                  }}
                >
                  {editorTab === 0 && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          System Prompt
                        </Typography>
                        <Tooltip title="AI Refine">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openRefineDialog("system")}
                              disabled={!systemPrompt}
                              sx={{
                                color: "white",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={25}
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        placeholder="Enter system prompt..."
                        variant="outlined"
                        spellCheck={false}
                        sx={{
                          "& .MuiInputBase-root": {
                            fontFamily: "monospace",
                            fontSize: 13,
                            bgcolor: "background.paper",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "divider",
                            },
                            "&:hover fieldset": {
                              borderColor: "divider",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "divider",
                            },
                          },
                          "& textarea": {
                            background: `linear-gradient(to right, 
                            transparent 0%, 
                            transparent calc(100% - 1px), 
                            transparent 100%
                          )`,
                            "&::selection": {
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                            },
                          },
                        }}
                        InputProps={{
                          style: {
                            color: "inherit",
                          },
                          inputComponent: "textarea",
                          inputProps: {
                            style: {
                              whiteSpace: "pre-wrap",
                              wordWrap: "break-word",
                            },
                          },
                        }}
                      />
                    </Box>
                  )}

                  {editorTab === 1 && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          User Prompt Template
                        </Typography>
                        <Tooltip title="AI Refine">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openRefineDialog("user")}
                              disabled={!userPromptTemplate}
                              sx={{
                                color: "white",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={25}
                        value={userPromptTemplate}
                        onChange={(e) => setUserPromptTemplate(e.target.value)}
                        placeholder="Enter user prompt template..."
                        variant="outlined"
                        spellCheck={false}
                        sx={{
                          "& .MuiInputBase-root": {
                            fontFamily: "monospace",
                            fontSize: 13,
                            bgcolor: "background.paper",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "divider",
                            },
                            "&:hover fieldset": {
                              borderColor: "divider",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "divider",
                            },
                          },
                        }}
                        helperText={
                          <Typography
                            variant="caption"
                            sx={{ color: "warning.main", fontSize: "0.7rem" }}
                          >
                            Variables in {"{curly braces}"} will be replaced at runtime
                          </Typography>
                        }
                      />
                    </Box>
                  )}

                  {editorTab === 2 && (
                    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                      {/* View Mode Toggle */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          mb: 1,
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        >
                          {selectedSchemaType || "Output Schema"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Code View">
                            <IconButton
                              size="small"
                              onClick={() => setSchemaViewMode("code")}
                              sx={{
                                color: schemaViewMode === "code" ? "white" : "text.disabled",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <CodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Card View">
                            <IconButton
                              size="small"
                              onClick={() => setSchemaViewMode("card")}
                              sx={{
                                color: schemaViewMode === "card" ? "white" : "text.disabled",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <ViewModuleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Code View */}
                      {schemaViewMode === "code" && (
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                          <Editor
                            key={`output-schema-${selectedSchemaType || "new"}`}
                            height="100%"
                            language="json"
                            theme="vs-dark"
                            value={outputSchema || ""}
                            onChange={(value) => setOutputSchema(value || "")}
                            options={{ minimap: { enabled: false }, fontSize: 13 }}
                          />
                        </Box>
                      )}

                      {/* Card View */}
                      {schemaViewMode === "card" && (
                        <Box sx={{ flex: 1, overflow: "auto", pb: 2, pr: 1 }}>
                          {(() => {
                            try {
                              const schema = JSON.parse(outputSchema || "{}");

                              // Recursive function to extract only leaf properties
                              const extractAllProperties = (obj, parentPath = "") => {
                                const allProps = [];

                                if (obj.properties) {
                                  Object.entries(obj.properties).forEach(([key, prop]) => {
                                    const fullPath = parentPath ? `${parentPath}.${key}` : key;

                                    // Only add leaf properties (no nested objects/arrays with properties)
                                    const isLeaf =
                                      !(prop.type === "object" && prop.properties) &&
                                      !(prop.type === "array" && prop.items?.properties);

                                    if (isLeaf) {
                                      allProps.push({
                                        path: fullPath,
                                        key: key,
                                        parentPath: parentPath,
                                        prop: prop,
                                        required: obj.required?.includes(key) || false,
                                      });
                                    }

                                    // Recursively get nested properties
                                    if (prop.type === "object" && prop.properties) {
                                      allProps.push(...extractAllProperties(prop, fullPath));
                                    }

                                    // Handle array items that have properties
                                    if (prop.type === "array" && prop.items?.properties) {
                                      allProps.push(
                                        ...extractAllProperties(prop.items, `${fullPath}[]`),
                                      );
                                    }
                                  });
                                }

                                return allProps;
                              };

                              const allProperties = extractAllProperties(schema);

                              return (
                                <Box
                                  sx={{ display: "flex", flexDirection: "column", gap: 1, pr: 1 }}
                                >
                                  {allProperties.map(
                                    ({ path, key, parentPath, prop, required }) => (
                                      <Paper
                                        key={path}
                                        sx={{
                                          p: 1.5,
                                          border: 1,
                                          borderColor: "divider",
                                          bgcolor: "background.paper",
                                          ml: parentPath ? parentPath.split(".").length * 2 : 0,
                                          height: 40,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 1,
                                            width: "100%",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              flex: 1,
                                              minWidth: 0,
                                            }}
                                          >
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 600,
                                                fontSize: "0.8rem",
                                                flexShrink: 0,
                                              }}
                                            >
                                              {key}
                                            </Typography>
                                            {(() => {
                                              const constraints = [];
                                              const fullConstraints = [];

                                              if (prop.enum) {
                                                const enumList = prop.enum.join(", ");
                                                const maxEnumDisplay = 50;
                                                if (enumList.length > maxEnumDisplay) {
                                                  constraints.push(
                                                    `Enum: ${enumList.substring(
                                                      0,
                                                      maxEnumDisplay,
                                                    )}...`,
                                                  );
                                                  fullConstraints.push(`Enum: ${enumList}`);
                                                } else {
                                                  constraints.push(`Enum: ${enumList}`);
                                                }
                                              }
                                              if (prop.minimum !== undefined)
                                                constraints.push(`Min: ${prop.minimum}`);
                                              if (prop.maximum !== undefined)
                                                constraints.push(`Max: ${prop.maximum}`);
                                              if (prop.minLength !== undefined)
                                                constraints.push(`MinLen: ${prop.minLength}`);
                                              if (prop.maxLength !== undefined)
                                                constraints.push(`MaxLen: ${prop.maxLength}`);
                                              if (prop.pattern)
                                                constraints.push(`Pattern: ${prop.pattern}`);
                                              if (prop.format)
                                                constraints.push(`Format: ${prop.format}`);
                                              if (prop.default !== undefined)
                                                constraints.push(
                                                  `Default: ${JSON.stringify(prop.default)}`,
                                                );
                                              if (prop.items)
                                                constraints.push(
                                                  `Items: ${prop.items.type || "object"}`,
                                                );

                                              const fullText =
                                                fullConstraints.length > 0
                                                  ? fullConstraints
                                                      .concat(
                                                        constraints.slice(fullConstraints.length),
                                                      )
                                                      .join(" | ")
                                                  : constraints.join(" | ");

                                              return constraints.length > 0 ? (
                                                <>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      color: "text.disabled",
                                                      mx: 0.5,
                                                    }}
                                                  >
                                                    |
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                    title={fullText}
                                                  >
                                                    {constraints.join(", ")}
                                                  </Typography>
                                                </>
                                              ) : null;
                                            })()}
                                          </Box>
                                          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                                            <Chip
                                              label={prop.type || "any"}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                              sx={{ height: 18, fontSize: "0.65rem" }}
                                            />
                                            {required && (
                                              <Chip
                                                label="Req"
                                                size="small"
                                                color="error"
                                                sx={{ height: 18, fontSize: "0.65rem" }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      </Paper>
                                    ),
                                  )}
                                  {allProperties.length === 0 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ textAlign: "center", py: 4 }}
                                    >
                                      No properties defined in schema
                                    </Typography>
                                  )}
                                </Box>
                              );
                            } catch (e) {
                              return (
                                <Alert severity="error" sx={{ m: 2 }}>
                                  Invalid JSON: {e.message}
                                </Alert>
                              );
                            }
                          })()}
                        </Box>
                      )}
                    </Box>
                  )}

                  {editorTab === 4 && (
                    <Box sx={{ maxWidth: 600 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                        Model Configuration
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Model
                          </Typography>
                          <FormControl fullWidth>
                            <Select
                              value={modelConfig.model}
                              onChange={(e) =>
                                setModelConfig({ ...modelConfig, model: e.target.value })
                              }
                              size="small"
                            >
                              <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                              <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                              <MenuItem value="gpt-4-turbo">gpt-4-turbo</MenuItem>
                              <MenuItem value="gpt-3.5-turbo">gpt-3.5-turbo</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Temperature
                          </Typography>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                            value={modelConfig.temperature}
                            onChange={(e) =>
                              setModelConfig({
                                ...modelConfig,
                                temperature: parseFloat(e.target.value),
                              })
                            }
                            helperText="Range: 0.0 to 2.0 (higher = more creative)"
                          />
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Max Tokens
                          </Typography>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            inputProps={{ min: 100, step: 100 }}
                            value={modelConfig.max_tokens || ""}
                            onChange={(e) =>
                              setModelConfig({
                                ...modelConfig,
                                max_tokens: parseInt(e.target.value) || null,
                              })
                            }
                            helperText="Leave empty for model default"
                          />
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Frontend Return
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={returnToFrontend}
                                onChange={(e) => setReturnToFrontend(e.target.checked)}
                              />
                            }
                            label="Return this schema output to frontend"
                          />
                          <Typography variant="body2" color="text.secondary">
                            Saves the return_to_frontend column for this schema.
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Description
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description of this schema..."
                            size="small"
                          />
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Notes
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Additional notes, change log, or implementation details..."
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {editorTab === 3 && (
                    <Box sx={{ width: "100%", height: "100%", overflowX: "auto", overflowY: "hidden", pb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        height: "100%",
                        minHeight: 640,
                        gap: 1.5,
                        minWidth: 940,
                      }}
                    >
                      <Paper
                        sx={{
                          width: 260,
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{ px: 1.25, py: 0.9, borderBottom: 1, borderColor: "divider" }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Search Target Column"
                            value={constraintTargetSearch}
                            onChange={(e) => setConstraintTargetSearch(e.target.value)}
                            disabled={!selectedSchemaType}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              display: "block",
                              fontWeight: 700,
                              color: "text.secondary",
                              letterSpacing: 0.5,
                            }}
                          >
                            TARGET COLUMNS ({filteredConstraintTargetGroups.length})
                          </Typography>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                          {loadingConstraintMaster ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                              <CircularProgress size={28} />
                            </Box>
                          ) : !selectedSchemaType ? (
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Select a schema first
                              </Typography>
                            </Box>
                          ) : filteredConstraintTargetGroups.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                No target columns found
                              </Typography>
                            </Box>
                          ) : (
                            <List dense sx={{ p: 0 }}>
                              {filteredConstraintTargetGroups.map((group) => {
                                const targetSwitchKey = `target:${selectedConstraintComponent}:${group.targetField}`;
                                const isFullyActive =
                                  group.count > 0 && group.activeCount === group.count;
                                const isDraftOnly = group.draftCount > 0 && group.draftCount === group.count;
                                const isUpdating =
                                  constraintMasterBulkSavingKey === targetSwitchKey;
                                return (
                                  <ListItem
                                    key={group.targetField}
                                    disablePadding
                                    secondaryAction={
                                      <Switch
                                        edge="end"
                                        size="small"
                                        checked={isFullyActive}
                                        disabled={
                                          loadingConstraintMaster ||
                                          !!constraintMasterBulkSavingKey ||
                                          isDraftOnly
                                        }
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={(event) => {
                                          event.stopPropagation();
                                          void handleConstraintMasterBulkStatusUpdate(
                                            "target",
                                            event.target.checked,
                                            {
                                              component: selectedConstraintComponent,
                                              targetField: group.targetField,
                                            },
                                          );
                                        }}
                                        sx={{ transform: "scale(0.8)" }}
                                      />
                                    }
                                  >
                                    <ListItemButton
                                      sx={{ pr: 7, py: 0.65 }}
                                      selected={selectedConstraintTarget === group.targetField}
                                      onClick={() => handleConstraintTargetSelect(group.targetField)}
                                    >
                                      <ListItemText
                                        primary={group.targetField}
                                        secondary={
                                          isUpdating
                                            ? "Updating..."
                                            : group.draftCount > 0
                                              ? `${group.activeCount}/${group.count} active | ${group.draftCount} draft`
                                            : `${group.activeCount}/${group.count} active`
                                        }
                                        primaryTypographyProps={{
                                          noWrap: true,
                                          fontSize: "0.9rem",
                                        }}
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                );
                              })}
                            </List>
                          )}
                        </Box>
                      </Paper>

                      <Box sx={{ flex: 1, minWidth: 640, display: "flex", flexDirection: "column" }}>
                        <Paper
                          sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                              {selectedTower && selectedCommodity
                                ? `${selectedTower}/${selectedCommodity}${
                                    selectedConstraintComponent
                                      ? `/${selectedConstraintComponent}`
                                      : ""
                                  }${
                                    selectedConstraintTarget
                                      ? `/${selectedConstraintTarget}`
                                      : ""
                                  }`
                                : "Select Tower and Commodity"}
                            </Typography>
                            {selectedConstraintComponent && (
                              <Typography variant="body2" color="text.secondary">
                                Component: {selectedConstraintComponentActiveCount}/
                                {selectedConstraintComponentRows.length} active
                                {selectedConstraintTarget
                                  ? ` | Target: ${selectedConstraintTargetActiveCount}/${constraintMasterTableRows.length} active`
                                  : ""}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Tooltip title="Refresh">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    selectedTower &&
                                    selectedCommodity &&
                                    fetchConstraintMasterItems(selectedTower, selectedCommodity)
                                  }
                                  disabled={
                                    !selectedTower ||
                                    !selectedCommodity ||
                                    loadingConstraintMaster ||
                                    !!constraintMasterBulkSavingKey
                                  }
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Add Target Column">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={handleConstraintMasterOpenCreateTarget}
                                  disabled={
                                    !selectedTower ||
                                    !selectedCommodity ||
                                    !selectedConstraintComponent ||
                                    !!constraintMasterBulkSavingKey
                                  }
                                  color="primary"
                                >
                                  <ViewColumnIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Add Category">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={handleConstraintMasterOpenCreate}
                                  disabled={
                                    !selectedTower ||
                                    !selectedCommodity ||
                                    !selectedConstraintComponent ||
                                    !!constraintMasterBulkSavingKey
                                  }
                                  color="primary"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Paper>

                        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 2 }}>
                          {loadingConstraintMaster ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                              }}
                            >
                              <CircularProgress size={36} />
                            </Box>
                          ) : !selectedConstraintComponent ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Select a schema to view target columns
                              </Typography>
                            </Box>
                          ) : !selectedConstraintTarget ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Select or add a target column
                              </Typography>
                            </Box>
                          ) : (
                            <Paper
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  Categories For {selectedConstraintTarget}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Showing all source categories under the selected component and
                                  target column.
                                </Typography>
                              </Box>

                              {constraintMasterTableRows.length === 0 ? (
                                <Box sx={{ p: 3 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No categories found for this target column.
                                  </Typography>
                                </Box>
                              ) : (
                                <TableContainer sx={{ overflowX: "auto" }}>
                                  <Table size="small" sx={{ minWidth: 820 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Source Category
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          Allowed Values
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Min</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Max</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Guidance</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="center">
                                          Edit
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {constraintMasterTableRows.map((row) => (
                                        <TableRow key={row.id} hover>
                                          <TableCell>
                                            {row.__draft ? (
                                              <Chip size="small" label="Draft" color="warning" />
                                            ) : (
                                              row.id
                                            )}
                                          </TableCell>
                                          <TableCell>{row.source_category || "-"}</TableCell>
                                          <TableCell>
                                            <Tooltip title={row.allowed_values || ""}>
                                              <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{ maxWidth: 220 }}
                                              >
                                                {row.allowed_values || "-"}
                                              </Typography>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell>
                                            {row.min_value === null || row.min_value === undefined
                                              ? "-"
                                              : row.min_value}
                                          </TableCell>
                                          <TableCell>
                                            {row.max_value === null || row.max_value === undefined
                                              ? "-"
                                              : row.max_value}
                                          </TableCell>
                                          <TableCell>
                                            <Tooltip title={row.guidance_text || ""}>
                                              <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{ maxWidth: 360 }}
                                              >
                                                {row.guidance_text || "-"}
                                              </Typography>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                                          <TableCell align="center">
                                            <Tooltip
                                              title={
                                                row.__draft
                                                  ? "Draft row will be written when you save"
                                                  : "Edit"
                                              }
                                            >
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleConstraintMasterOpenEdit(row)}
                                                  disabled={!!row.__draft}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                            </Paper>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    </Box>
                  )}

                  {false && editorTab === 3 && (
                    <Box sx={{ display: "flex", height: "100%", gap: 2 }}>
                      {/* Left Navigator - Constraint Rules by condition_json */}
                      <Paper
                        sx={{
                          width: 280,
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "primary.main",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "white" }}
                              >
                                Constraint Rules
                              </Typography>
                              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                                {constraintRules.length} rule
                                {constraintRules.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                            <Tooltip title="Add New Rule">
                              <IconButton
                                size="small"
                                sx={{ color: "white" }}
                                onClick={() => setNewRuleDialogOpen(true)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {loadingConstraintRules ? (
                          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                            <CircularProgress size={30} />
                          </Box>
                        ) : constraintRules.length === 0 ? (
                          <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              No constraint rules found
                            </Typography>
                          </Box>
                        ) : (
                          <List sx={{ flex: 1, overflow: "auto", p: 0 }}>
                            {constraintRules.map((rule) => {
                              // Parse and format condition JSON for display
                              let conditionDisplay = rule.condition_json;
                              try {
                                const parsed = JSON.parse(rule.condition_json);
                                // Extract key-value pairs from the condition JSON
                                const entries = Object.entries(parsed);
                                if (entries.length > 0) {
                                  // Format as: field | value (e.g., "country | USA")
                                  const [key, value] = entries[0];
                                  conditionDisplay = `${key} | ${value}`;
                                }
                              } catch (e) {
                                // If parsing fails, use the raw string
                                conditionDisplay = rule.condition_json;
                              }

                              return (
                                <ListItem key={rule.rule_id} disablePadding>
                                  <ListItemButton
                                    selected={selectedConstraintRule?.rule_id === rule.rule_id}
                                    onClick={() => {
                                      setSelectedConstraintRule(rule);
                                      // Exit edit mode when selecting a different rule
                                      setEditingRule(false);
                                      setEditedRuleData(null);
                                      setEditingSection(null);
                                      setEditedConstraints({});
                                      setEditingJsonView(false);
                                      setEditedJsonContent("");
                                    }}
                                    sx={{
                                      py: 0.75,
                                      px: 2,
                                      borderLeft: 3,
                                      borderColor:
                                        selectedConstraintRule?.rule_id === rule.rule_id
                                          ? "primary.main"
                                          : "transparent",
                                    }}
                                  >
                                    <ListItemText
                                      primary={`#${rule.rule_id}: ${conditionDisplay}`}
                                      primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight:
                                          selectedConstraintRule?.rule_id === rule.rule_id
                                            ? 600
                                            : 400,
                                        fontSize: "0.85rem",
                                        sx: {
                                          overflow: "visible",
                                          textOverflow: "clip",
                                          whiteSpace: "normal",
                                          display: "block",
                                          wordBreak: "break-word",
                                        },
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                      </Paper>

                      {/* Right Content - Selected Rule Details */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: "1000px",
                        }}
                      >
                        {selectedConstraintRule ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              height: "100%",
                            }}
                          >
                            <Box>
                              {editingRule ? (
                                <Paper sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      Edit Rule #{selectedConstraintRule.rule_id}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Tooltip title="Save">
                                        <IconButton color="success" onClick={saveRuleEdit}>
                                          <CheckIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel">
                                        <IconButton
                                          onClick={() => {
                                            setEditingRule(false);
                                            setEditedRuleData(null);
                                          }}
                                        >
                                          <CloseIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <TextField
                                      label="Rule ID"
                                      type="number"
                                      value={editedRuleData?.rule_id || ""}
                                      onChange={(e) =>
                                        setEditedRuleData({
                                          ...editedRuleData,
                                          rule_id: parseInt(e.target.value) || 0,
                                        })
                                      }
                                      size="small"
                                      fullWidth
                                    />
                                    <TextField
                                      label="Priority"
                                      type="number"
                                      value={editedRuleData?.priority || ""}
                                      onChange={(e) =>
                                        setEditedRuleData({
                                          ...editedRuleData,
                                          priority: parseInt(e.target.value) || 0,
                                        })
                                      }
                                      size="small"
                                      fullWidth
                                      helperText="Lower number = higher precedence"
                                    />
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                      <Typography variant="body2">Active:</Typography>
                                      <Switch
                                        checked={editedRuleData?.active || false}
                                        onChange={(e) =>
                                          setEditedRuleData({
                                            ...editedRuleData,
                                            active: e.target.checked,
                                          })
                                        }
                                      />
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Condition
                                      </Typography>
                                      {editedRuleData?.condition_json &&
                                        typeof editedRuleData.condition_json === "object" && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 1,
                                            }}
                                          >
                                            {Object.entries(editedRuleData.condition_json).map(
                                              ([key, value]) => (
                                                <Box key={key}>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ color: "text.secondary" }}
                                                  >
                                                    {key}
                                                  </Typography>
                                                  <TextField
                                                    value={value}
                                                    onChange={(e) =>
                                                      setEditedRuleData({
                                                        ...editedRuleData,
                                                        condition_json: {
                                                          ...editedRuleData.condition_json,
                                                          [key]: e.target.value,
                                                        },
                                                      })
                                                    }
                                                    size="small"
                                                    fullWidth
                                                  />
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        )}
                                    </Box>
                                  </Box>
                                </Paper>
                              ) : (
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      Rule #{selectedConstraintRule.rule_id}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Tooltip title="Edit Rule">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => {
                                            // Parse condition_json if it's a string
                                            let conditionJson =
                                              selectedConstraintRule.condition_json;
                                            if (typeof conditionJson === "string") {
                                              try {
                                                conditionJson = JSON.parse(conditionJson);
                                              } catch (e) {
                                                console.error("Failed to parse condition_json:", e);
                                                conditionJson = {};
                                              }
                                            }

                                            setEditingRule(true);
                                            setEditedRuleData({
                                              rule_id: selectedConstraintRule.rule_id,
                                              priority: selectedConstraintRule.priority,
                                              active: selectedConstraintRule.active,
                                              condition_json: conditionJson,
                                            });
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete Rule">
                                        <IconButton size="small" color="error">
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                    <Chip
                                      label={`Tower: ${selectedConstraintRule.tower}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Commodity: ${selectedConstraintRule.commodity}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Schema: ${selectedConstraintRule.schema_type}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Priority: ${selectedConstraintRule.priority}`}
                                      size="small"
                                      color="primary"
                                    />
                                  </Box>
                                  {selectedConstraintRule.notes && (
                                    <Typography variant="body2" color="text.secondary">
                                      {selectedConstraintRule.notes}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Constraints
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                  {constraintViewMode === "json" && (
                                    <>
                                      {editingJsonView ? (
                                        <>
                                          <Tooltip title="Save">
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={async () => {
                                                try {
                                                  // Validate JSON
                                                  const parsed = JSON.parse(editedJsonContent);

                                                  // Save to backend
                                                  const response = await apiFetch(
                                                    `${API_BASE}/constraint-rules/${selectedConstraintRule.rule_id}`,
                                                    {
                                                      method: "PUT",
                                                      headers: {
                                                        "Content-Type": "application/json",
                                                      },
                                                      body: JSON.stringify({
                                                        constraints_json: parsed,
                                                      }),
                                                    },
                                                  );

                                                  const data = await response.json();

                                                  if (data.status === "success") {
                                                    setSuccess("Constraints updated successfully");
                                                    setEditingJsonView(false);
                                                    await loadConstraintRules(selectedSchemaType);
                                                    const updatedRule = constraintRules.find(
                                                      (r) =>
                                                        r.rule_id ===
                                                        selectedConstraintRule.rule_id,
                                                    );
                                                    if (updatedRule) {
                                                      setSelectedConstraintRule(updatedRule);
                                                    }
                                                  } else {
                                                    setError(
                                                      data.error || "Failed to update constraints",
                                                    );
                                                  }
                                                } catch (error) {
                                                  setError(
                                                    `Invalid JSON or save failed: ${error.message}`,
                                                  );
                                                }
                                              }}
                                            >
                                              <CheckIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Cancel">
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setEditingJsonView(false);
                                                setEditedJsonContent("");
                                              }}
                                            >
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      ) : (
                                        <Tooltip title="Edit JSON">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              setEditingJsonView(true);
                                              setEditedJsonContent(
                                                selectedConstraintRule.constraints_json,
                                              );
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                  <ToggleButtonGroup
                                    value={constraintViewMode}
                                    exclusive
                                    onChange={(e, newMode) =>
                                      newMode && setConstraintViewMode(newMode)
                                    }
                                    size="small"
                                  >
                                    <ToggleButton value="json">
                                      <CodeIcon fontSize="small" />
                                    </ToggleButton>
                                    <ToggleButton value="card">
                                      <ViewListIcon fontSize="small" />
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </Box>
                              </Box>
                              {constraintViewMode === "json" ? (
                                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      border: 1,
                                      borderColor: "divider",
                                      borderRadius: 1,
                                      position: "relative",
                                    }}
                                  >
                                    {editingJsonView ? (
                                      <Editor
                                        key="editing"
                                        height="100%"
                                        defaultLanguage="json"
                                        defaultValue={editedJsonContent}
                                        onChange={(value) => setEditedJsonContent(value || "")}
                                        theme="vs-dark"
                                        options={{
                                          minimap: { enabled: false },
                                          fontSize: 13,
                                          lineNumbers: "on",
                                          scrollBeyondLastLine: false,
                                          automaticLayout: true,
                                        }}
                                      />
                                    ) : (
                                      <Editor
                                        key="readonly"
                                        height="100%"
                                        defaultLanguage="json"
                                        value={selectedConstraintRule.constraints_json}
                                        theme="vs-dark"
                                        options={{
                                          readOnly: true,
                                          minimap: { enabled: false },
                                          fontSize: 13,
                                          lineNumbers: "on",
                                          scrollBeyondLastLine: false,
                                          automaticLayout: true,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ flex: 1, overflow: "auto" }}>
                                  {(() => {
                                    try {
                                      const constraints = JSON.parse(
                                        selectedConstraintRule.constraints_json,
                                      );
                                      return Object.entries(constraints).map(
                                        ([key, value], index) => (
                                          <Accordion
                                            key={index}
                                            defaultExpanded={index === 0}
                                            sx={{ mb: 0.5 }}
                                          >
                                            <AccordionSummary
                                              expandIcon={<ExpandMoreIcon />}
                                              sx={{ py: 0.5, minHeight: "40px" }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                  width: "100%",
                                                  mr: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  {key}
                                                </Typography>
                                                <Box
                                                  sx={{ display: "flex", gap: 0.5 }}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <Tooltip title="Edit Section">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => {
                                                        setEditingSection(key);
                                                        setEditedConstraints({ ...constraints });
                                                      }}
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Delete Section">
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={() => {
                                                        const updated = { ...constraints };
                                                        delete updated[key];
                                                        // Here you would call an API to update the constraint
                                                        console.log("Delete section:", key);
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ py: 1, px: 2 }}>
                                              {editingSection === key ? (
                                                <Box>
                                                  {typeof value === "object" && value !== null ? (
                                                    Array.isArray(value) ? (
                                                      <Box>
                                                        {editedConstraints[key]?.map(
                                                          (item, idx) => (
                                                            <Box
                                                              key={idx}
                                                              sx={{
                                                                display: "flex",
                                                                gap: 1,
                                                                mb: 1,
                                                                alignItems: "center",
                                                              }}
                                                            >
                                                              <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={item}
                                                                onChange={(e) => {
                                                                  const updated = [
                                                                    ...editedConstraints[key],
                                                                  ];
                                                                  updated[idx] = e.target.value;
                                                                  setEditedConstraints({
                                                                    ...editedConstraints,
                                                                    [key]: updated,
                                                                  });
                                                                }}
                                                              />
                                                              <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => {
                                                                  const updated = editedConstraints[
                                                                    key
                                                                  ].filter((_, i) => i !== idx);
                                                                  setEditedConstraints({
                                                                    ...editedConstraints,
                                                                    [key]: updated,
                                                                  });
                                                                }}
                                                              >
                                                                <DeleteIcon fontSize="small" />
                                                              </IconButton>
                                                            </Box>
                                                          ),
                                                        )}
                                                        <Button
                                                          size="small"
                                                          startIcon={<AddIcon />}
                                                          onClick={() => {
                                                            setEditedConstraints({
                                                              ...editedConstraints,
                                                              [key]: [
                                                                ...editedConstraints[key],
                                                                "",
                                                              ],
                                                            });
                                                          }}
                                                        >
                                                          Add Item
                                                        </Button>
                                                      </Box>
                                                    ) : (
                                                      <Box>
                                                        {Object.entries(editedConstraints[key]).map(
                                                          ([subKey, subValue]) => (
                                                            <Box key={subKey} sx={{ mb: 1.5 }}>
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  fontWeight: 600,
                                                                  display: "block",
                                                                  mb: 0.5,
                                                                }}
                                                              >
                                                                {subKey}
                                                              </Typography>
                                                              <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={
                                                                  typeof subValue === "object"
                                                                    ? JSON.stringify(subValue)
                                                                    : subValue
                                                                }
                                                                onChange={(e) => {
                                                                  setEditedConstraints({
                                                                    ...editedConstraints,
                                                                    [key]: {
                                                                      ...editedConstraints[key],
                                                                      [subKey]: e.target.value,
                                                                    },
                                                                  });
                                                                }}
                                                              />
                                                            </Box>
                                                          ),
                                                        )}
                                                      </Box>
                                                    )
                                                  ) : (
                                                    <TextField
                                                      size="small"
                                                      fullWidth
                                                      value={editedConstraints[key]}
                                                      onChange={(e) => {
                                                        setEditedConstraints({
                                                          ...editedConstraints,
                                                          [key]: e.target.value,
                                                        });
                                                      }}
                                                    />
                                                  )}
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      gap: 1,
                                                      justifyContent: "flex-end",
                                                      mt: 2,
                                                    }}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      color="success"
                                                      onClick={() => saveConstraintSection(key)}
                                                    >
                                                      <CheckIcon />
                                                    </IconButton>
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => setEditingSection(null)}
                                                    >
                                                      <CloseIcon />
                                                    </IconButton>
                                                  </Box>
                                                </Box>
                                              ) : typeof value === "object" && value !== null ? (
                                                Array.isArray(value) ? (
                                                  <Box>
                                                    {value.map((item, idx) => (
                                                      <Typography
                                                        key={idx}
                                                        variant="body2"
                                                        sx={{ py: 0.25, pl: 1 }}
                                                      >
                                                        • {item}
                                                      </Typography>
                                                    ))}
                                                  </Box>
                                                ) : (
                                                  Object.entries(value).map(
                                                    ([subKey, subValue]) => (
                                                      <Box
                                                        key={subKey}
                                                        sx={{
                                                          display: "flex",
                                                          mb: 0.5,
                                                          alignItems: "baseline",
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          color="text.secondary"
                                                          sx={{
                                                            fontWeight: 600,
                                                            minWidth: "120px",
                                                            mr: 1,
                                                          }}
                                                        >
                                                          {subKey}:
                                                        </Typography>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ flex: 1 }}
                                                        >
                                                          {Array.isArray(subValue)
                                                            ? subValue.join(", ")
                                                            : typeof subValue === "object"
                                                              ? JSON.stringify(subValue, null, 2)
                                                              : String(subValue)}
                                                        </Typography>
                                                      </Box>
                                                    ),
                                                  )
                                                )
                                              ) : (
                                                <Typography variant="body2">
                                                  {String(value)}
                                                </Typography>
                                              )}
                                            </AccordionDetails>
                                          </Accordion>
                                        ),
                                      );
                                    } catch (e) {
                                      return <Alert severity="error">Invalid JSON format</Alert>;
                                    }
                                  })()}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Select a constraint rule to view details
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Right Panel - Versions & AI Assistant */}
              <Box
                sx={{
                  width: rightPanelCollapsed ? 48 : 360,
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  borderLeft: 1,
                  borderColor: "divider",
                  overflow: "hidden",
                  transition: "width 180ms ease",
                }}
              >
                {rightPanelCollapsed ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <Tooltip title="Expand AI panel">
                      <IconButton size="small" onClick={() => setRightPanelCollapsed(false)}>
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider flexItem />
                    <Tooltip title="AI Assistant">
                      <IconButton
                        size="small"
                        color={activeTab === 0 ? "primary" : "default"}
                        onClick={() => {
                          setRightPanelCollapsed(false);
                          setActiveTab(0);
                          if (selectedSchemaType) {
                            openAiEditSchemaDialog();
                          }
                        }}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Versions">
                      <IconButton
                        size="small"
                        color={activeTab === 1 ? "primary" : "default"}
                        onClick={() => {
                          setRightPanelCollapsed(false);
                          setActiveTab(1);
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <>
                <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
                  <Tooltip title="Collapse AI panel">
                    <IconButton
                      size="small"
                      onClick={() => setRightPanelCollapsed(true)}
                      sx={{ ml: 0.5, mr: 0.25, flexShrink: 0 }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tabs
                    value={activeTab}
                    onChange={(e, v) => {
                      setActiveTab(v);
                      if (v === 0 && selectedSchemaType) {
                        openAiEditSchemaDialog();
                      }
                    }}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Tab label="AI Assistant" icon={<AutoAwesomeIcon />} iconPosition="start" />
                    <Tab label="Versions" icon={<HistoryIcon />} iconPosition="start" />
                  </Tabs>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: activeTab === 0 ? "hidden" : "auto",
                    p: activeTab === 0 ? 0 : 1.5,
                    pb: activeTab === 0 ? 0 : 2,
                  }}
                >
                  {activeTab === 1 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        RESTORE POINTS
                      </Typography>
                      {currentRestorePoints.map((restorePoint, index) => {
                        const isSelected = selectedRestorePointId === restorePoint.id;
                        const restorePointTitle =
                          restorePoint.phase === "original"
                            ? "Original"
                            : `Restore Point ${getRestorePointNumber(restorePoint) || index + 1}`;
                        return (
                          <Paper
                            key={restorePoint.id}
                            elevation={isSelected ? 2 : 0}
                            sx={{
                              p: 1.25,
                              border: 1,
                              borderColor: isSelected ? "primary.main" : "divider",
                              bgcolor: isSelected ? "action.selected" : "background.paper",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                borderColor: "primary.main",
                                bgcolor: "action.hover",
                              },
                            }}
                            onClick={() => handleRestorePointSelect(restorePoint)}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                                {restorePointTitle}
                              </Typography>
                              {isSelected && <Chip label="Loaded" size="small" color="primary" />}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {restorePoint.label || "Restore point"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              {new Date(restorePoint.createdAt).toLocaleString()}
                            </Typography>
                          </Paper>
                        );
                      })}
                      {currentRestorePoints.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          No restore points yet
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        VERSIONS
                      </Typography>
                      {versions.map((version) => (
                        <Paper
                          key={version.id}
                          elevation={version.is_active ? 2 : 0}
                          sx={{
                            p: 1.5,
                            border: 1,
                            borderColor: version.is_active ? "grey.600" : "divider",
                            bgcolor: version.is_active ? "grey.800" : "background.paper",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderColor: "grey.600",
                              bgcolor: "action.hover",
                            },
                          }}
                          onClick={() => {
                            if (!version.is_active) {
                              setVersionToActivate(version.schema_version);
                              setActivateDialogOpen(true);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: version.is_active ? 600 : 400,
                                fontSize: "0.875rem",
                              }}
                            >
                              v{version.schema_version}
                            </Typography>
                            {version.is_active ? (
                              <Chip
                                label="Active"
                                size="small"
                                sx={{
                                  bgcolor: "grey.00",
                                  color: "grey.200",
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                }}
                              />
                            ) : (
                              <Chip label="Inactive" size="small" variant="outlined" />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {new Date(version.created_at).toLocaleString()}
                          </Typography>
                          {version.created_by && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", display: "block", mt: 0.5 }}
                            >
                              By: {version.created_by}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                      {versions.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          No versions available
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        AUDIT LOG
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {auditLog.map((log, index) => (
                          <React.Fragment key={index}>
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                              <ListItemText
                                primary={log.action}
                                secondary={
                                  <>
                                    <Typography variant="caption" display="block">
                                      {log.created_by} â€¢ {new Date(log.created_at).toLocaleString()}
                                    </Typography>
                                    {log.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {log.description}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                            {index < auditLog.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                      {auditLog.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          No audit entries available
                        </Typography>
                      )}
                    </Box>
                  )}

                  {activeTab === 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        minHeight: 0,
                      }}
                    >
                      {aiSchemaMode === "create" && (
                        <Alert severity="info" sx={{ m: 1.5, mb: aiSchemaError ? 0 : 1 }}>
                          Enter the schema type on the left, then describe what the schema should do here.
                        </Alert>
                      )}

                      {aiSchemaError && (
                        <Alert
                          severity="error"
                          onClose={() => setAiSchemaError(null)}
                          sx={{ m: 1.5, mb: 1 }}
                        >
                          {aiSchemaError}
                        </Alert>
                      )}

                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: "auto",
                          overflowX: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.5,
                        }}
                      >
                        {aiSchemaChatMessages.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {selectedSchemaType
                              ? "Ask AI to modify the selected schema."
                              : "Describe the new schema you want to generate."}
                          </Typography>
                        ) : (
                          aiSchemaChatMessages.map((message, index) => {
                            const restorePoint = getRestorePointForMessage(message);
                            const markerPosition =
                              message.restorePointPosition ||
                              (message.role === "assistant" ? "after" : "before");

                            return (
                              <React.Fragment key={`${message.role}-${index}`}>
                                {originalRestorePoint && index === firstUserMessageIndex
                                  ? renderRestorePointMarker(originalRestorePoint)
                                  : null}
                                {restorePoint && markerPosition !== "after"
                                  ? renderRestorePointMarker(restorePoint)
                                  : null}
                                <Box
                                  sx={{
                                    alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                                    maxWidth: "92%",
                                    p: 1.1,
                                    borderRadius: 1,
                                    bgcolor:
                                      message.role === "user"
                                        ? "primary.main"
                                        : "background.default",
                                    color:
                                      message.role === "user"
                                        ? "primary.contrastText"
                                        : "text.primary",
                                    border: message.role === "user" ? 0 : 1,
                                    borderColor: "divider",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", opacity: 0.75 }}
                                  >
                                    {message.role === "user" ? "You" : "AI"}
                                  </Typography>
                                  <Typography variant="body2">{message.content}</Typography>
                                </Box>
                                {restorePoint && markerPosition === "after"
                                  ? renderRestorePointMarker(restorePoint)
                                  : null}
                              </React.Fragment>
                            );
                          })
                        )}
                        {aiSchemaGenerating && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                              Updating editor...
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box
                        sx={{
                          flexShrink: 0,
                          p: 1.5,
                          pt: 1,
                          borderTop: 1,
                          borderColor: "divider",
                          bgcolor: "background.paper",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {aiSchemaDraft && (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 800,
                                  letterSpacing: 0.4,
                                  textTransform: "uppercase",
                                }}
                              >
                                Settings
                              </Typography>
                              <Tooltip title={aiSettingsExpanded ? "Collapse options" : "Expand options"}>
                                <IconButton
                                  size="small"
                                  onClick={() => setAiSettingsExpanded((expanded) => !expanded)}
                                  sx={{ width: 24, height: 24 }}
                                >
                                  <ExpandMoreIcon
                                    sx={{
                                      fontSize: 18,
                                      transform: aiSettingsExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                      transition: "transform 140ms ease",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>

                            {!aiSettingsExpanded && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.68rem" }}
                              >
                                Change: {aiChangeScope.length}/{AI_CHANGE_SCOPE_OPTIONS.length}; Constraints:{" "}
                                {aiConstraintsEnabled
                                  ? aiConstraintsRequested
                                    ? `${selectedAiMappingTypes.join(", ")} / ${selectedAiConstraintTypes.join(", ")}`
                                    : "choose types"
                                  : "off"}
                              </Typography>
                            )}

                            {aiSettingsExpanded && (
                              <>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontWeight: 800,
                                      letterSpacing: 0.4,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Change Only
                                  </Typography>
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={allAiChangeScopesSelected}
                                          onChange={handleAiChangeScopeAllToggle}
                                          disabled={aiSchemaGenerating || schemaTypeActionLoading}
                                          sx={{ p: 0.35 }}
                                        />
                                      }
                                      label="All"
                                      sx={{
                                        m: 0,
                                        mr: 0.75,
                                        "& .MuiFormControlLabel-label": {
                                          fontSize: "0.72rem",
                                          lineHeight: 1.2,
                                          color: "text.secondary",
                                        },
                                      }}
                                    />
                                    {AI_CHANGE_SCOPE_OPTIONS.map((option) => (
                                      <FormControlLabel
                                        key={option.value}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={aiChangeScope.includes(option.value)}
                                            onChange={() => handleAiChangeScopeToggle(option.value)}
                                            disabled={aiSchemaGenerating || schemaTypeActionLoading}
                                            sx={{ p: 0.35 }}
                                          />
                                        }
                                        label={option.label}
                                        sx={{
                                          m: 0,
                                          mr: 0.75,
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: "0.72rem",
                                            lineHeight: 1.2,
                                            color: "text.secondary",
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={aiConstraintsEnabled}
                                        onChange={(event) =>
                                          setAiConstraintsEnabled(event.target.checked)
                                        }
                                        disabled={aiSchemaGenerating || schemaTypeActionLoading}
                                        sx={{ p: 0.35 }}
                                      />
                                    }
                                    label="Constraints"
                                    sx={{
                                      m: 0,
                                      alignSelf: "flex-start",
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.72rem",
                                        lineHeight: 1.2,
                                        color: "text.secondary",
                                        fontWeight: 800,
                                        letterSpacing: 0.4,
                                        textTransform: "uppercase",
                                      },
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.68rem" }}
                                  >
                                    {aiConstraintsEnabled
                                      ? "Select the types the AI should use to construct new constraint rows."
                                      : "Enable constraints to let the AI create new constraint rows."}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 1,
                                      opacity: aiConstraintsEnabled ? 1 : 0.45,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ display: "block", fontSize: "0.66rem", mb: 0.25 }}
                                      >
                                        Mapping Type
                                      </Typography>
                                      {AI_MAPPING_TYPE_OPTIONS.map((option) => (
                                        <FormControlLabel
                                          key={option.value}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={selectedAiMappingTypes.includes(option.value)}
                                              onChange={() => handleAiMappingTypeToggle(option.value)}
                                              disabled={
                                                !aiConstraintsEnabled ||
                                                aiSchemaGenerating ||
                                                schemaTypeActionLoading
                                              }
                                              sx={{ p: 0.35 }}
                                            />
                                          }
                                          label={option.label}
                                          sx={{
                                            m: 0,
                                            mr: 0.5,
                                            display: "flex",
                                            "& .MuiFormControlLabel-label": {
                                              fontSize: "0.7rem",
                                              color: "text.secondary",
                                            },
                                          }}
                                        />
                                      ))}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ display: "block", fontSize: "0.66rem", mb: 0.25 }}
                                      >
                                        Constraint Type
                                      </Typography>
                                      {AI_CONSTRAINT_TYPE_OPTIONS.map((option) => (
                                        <FormControlLabel
                                          key={option.value}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={selectedAiConstraintTypes.includes(option.value)}
                                              onChange={() => handleAiConstraintTypeToggle(option.value)}
                                              disabled={
                                                !aiConstraintsEnabled ||
                                                aiSchemaGenerating ||
                                                schemaTypeActionLoading
                                              }
                                              sx={{ p: 0.35 }}
                                            />
                                          }
                                          label={option.label}
                                          sx={{
                                            m: 0,
                                            mr: 0.5,
                                            display: "flex",
                                            "& .MuiFormControlLabel-label": {
                                              fontSize: "0.7rem",
                                              color: "text.secondary",
                                            },
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                  {aiConstraintsEnabled && (
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                      sx={{ fontSize: "0.64rem" }}
                                    >
                                      {aiConstraintsRequested
                                        ? aiConstraintExampleRows.length
                                          ? `${aiConstraintExampleRows.length} matching existing row examples available.`
                                          : "AI will create new rows from the selected type pattern."
                                        : "Select at least one mapping type and one constraint type to create constraints."}
                                    </Typography>
                                  )}
                                </Box>
                              </>
                            )}
                          </Box>
                        )}

                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <TextField
                            fullWidth
                            label={aiSchemaDraft ? "Modify Draft" : "Describe Schema"}
                            value={aiSchemaDescription}
                            onChange={(e) => setAiSchemaDescription(e.target.value)}
                            placeholder={
                              aiSchemaDraft
                                ? "Ask AI to update only the selected sections..."
                                : "Describe fields, validation rules, required outputs..."
                            }
                            disabled={aiSchemaGenerating || schemaTypeActionLoading}
                            multiline
                            minRows={3}
                            maxRows={6}
                          />
                          <Tooltip title={aiSchemaDraft ? "Send Change" : "Generate Draft"}>
                            <span>
                              <IconButton
                                color="primary"
                                onClick={handleGenerateAiSchemaDraft}
                                disabled={
                                  !aiSchemaTypeName.trim() ||
                                  !aiSchemaDescription.trim() ||
                                  (aiSchemaDraft && aiChangeScope.length === 0 && !aiConstraintsRequested) ||
                                  aiSchemaGenerating ||
                                  schemaTypeActionLoading
                                }
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 1,
                                  bgcolor: "primary.main",
                                  color: "primary.contrastText",
                                  flexShrink: 0,
                                  "&:hover": { bgcolor: "primary.dark" },
                                  "&.Mui-disabled": {
                                    bgcolor: "action.disabledBackground",
                                    color: "action.disabled",
                                  },
                                }}
                              >
                                {aiSchemaGenerating ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : (
                                  <SendIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>

                        {aiSchemaMode === "create" ? (
                          <Button
                            variant="contained"
                            onClick={handleCreateAiSchemaType}
                            disabled={
                              !aiSchemaDraft ||
                              !aiSchemaTypeName.trim() ||
                              schemaTypeActionLoading ||
                              saving
                            }
                          >
                            Create Schema
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                  )}

                  {false && (
                    <List dense>
                      {auditLog.map((log, index) => (
                        <React.Fragment key={index}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={log.action}
                              secondary={
                                <>
                                  <Typography variant="caption" display="block">
                                    {log.created_by} • {new Date(log.created_at).toLocaleString()}
                                  </Typography>
                                  {log.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {log.description}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                          {index < auditLog.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a schema type to begin editing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a tower, commodity, and schema type from the left panel
              </Typography>
            </Box>
          )}
        </Box>
        </Box>

        {/* Create Schema Type Dialog */}
        <Dialog
          open={createSchemaDialogOpen}
          onClose={() => !schemaTypeActionLoading && setCreateSchemaDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New Schema Type</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {selectedTower}/{selectedCommodity} - {selectedSchemaStage}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Schema Type"
              value={newSchemaTypeName}
              onChange={(e) => setNewSchemaTypeName(e.target.value)}
              placeholder="e.g. qualityCheck"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newSchemaTypeDescription}
              onChange={(e) => setNewSchemaTypeDescription(e.target.value)}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCreateSchemaDialogOpen(false)}
              disabled={schemaTypeActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSchemaType}
              disabled={!newSchemaTypeName.trim() || schemaTypeActionLoading}
            >
              {schemaTypeActionLoading ? "Creating..." : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* AI Create Schema Type Dialog */}
        <Dialog
          open={aiSchemaDialogOpen}
          onClose={() =>
            !aiSchemaGenerating && !schemaTypeActionLoading && setAiSchemaDialogOpen(false)
          }
          fullWidth
          maxWidth={false}
          PaperProps={{
            sx: {
              width: "min(1280px, calc(100vw - 48px))",
              maxWidth: "none",
              height: "min(860px, calc(100vh - 48px))",
              maxHeight: "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {aiSchemaMode === "edit" ? "Modify Schema with AI" : "Create Schema with AI"}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setAiSchemaDialogOpen(false)}
                disabled={aiSchemaGenerating || schemaTypeActionLoading}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 380px" },
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  minWidth: 0,
                  bgcolor: "background.default",
                }}
              >
                {aiSchemaError && (
                  <Alert
                    severity="error"
                    sx={{ m: 2, mb: 0 }}
                    onClose={() => setAiSchemaError(null)}
                  >
                    {aiSchemaError}
                  </Alert>
                )}

                {!aiSchemaDraft ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        width: "min(680px, 100%)",
                        p: 4,
                        textAlign: "center",
                      }}
                    >
                      {aiSchemaGenerating ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={34} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Updating draft
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            The generated schema will appear in the editor tabs.
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <AutoAwesomeIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            No draft yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Start from the chat panel, then review the generated tabs here.
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ px: 2, pt: 2, borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        value={aiSchemaTab}
                        onChange={(e, value) => setAiSchemaTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                      >
                        <Tab label="System Prompt" />
                        <Tab label="User Prompt" />
                        <Tab label="Output Schema" />
                        <Tab label="Model Config" />
                        <Tab label="Details" />
                      </Tabs>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                      {aiSchemaTab === 0 && (
                        <TextField
                          fullWidth
                          label="System Prompt"
                          value={aiSchemaDraft.system_prompt}
                          onChange={(e) =>
                            setAiSchemaDraft((prev) => ({
                              ...prev,
                              system_prompt: e.target.value,
                            }))
                          }
                          multiline
                          minRows={22}
                          disabled={schemaTypeActionLoading}
                        />
                      )}

                      {aiSchemaTab === 1 && (
                        <TextField
                          fullWidth
                          label="User Prompt"
                          value={aiSchemaDraft.user_template}
                          onChange={(e) =>
                            setAiSchemaDraft((prev) => ({
                              ...prev,
                              user_template: e.target.value,
                            }))
                          }
                          multiline
                          minRows={22}
                          disabled={schemaTypeActionLoading}
                        />
                      )}

                      {aiSchemaTab === 2 && (
                        <Box
                          sx={{
                            height: "100%",
                            minHeight: 560,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Editor
                            height="100%"
                            language="json"
                            theme="vs-dark"
                            value={aiSchemaDraft.schema_json}
                            onChange={(value) =>
                              setAiSchemaDraft((prev) => ({
                                ...prev,
                                schema_json: value || "",
                              }))
                            }
                            options={{
                              automaticLayout: true,
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              wordWrap: "on",
                              tabSize: 2,
                              formatOnPaste: true,
                              formatOnType: true,
                            }}
                          />
                        </Box>
                      )}

                      {aiSchemaTab === 3 && (
                        <Paper variant="outlined" sx={{ p: 2, maxWidth: 760 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                            Model Configuration
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "1fr 160px 160px" },
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="Model"
                              value={aiSchemaDraft.model}
                              onChange={(e) =>
                                setAiSchemaDraft((prev) => ({ ...prev, model: e.target.value }))
                              }
                              disabled={schemaTypeActionLoading}
                            />
                            <TextField
                              fullWidth
                              type="number"
                              label="Temperature"
                              value={aiSchemaDraft.temperature}
                              onChange={(e) =>
                                setAiSchemaDraft((prev) => ({
                                  ...prev,
                                  temperature: e.target.value,
                                }))
                              }
                              disabled={schemaTypeActionLoading}
                            />
                            <TextField
                              fullWidth
                              type="number"
                              label="Max Tokens"
                              value={aiSchemaDraft.max_tokens ?? ""}
                              onChange={(e) =>
                                setAiSchemaDraft((prev) => ({
                                  ...prev,
                                  max_tokens: e.target.value,
                                }))
                              }
                              disabled={schemaTypeActionLoading}
                            />
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(aiSchemaDraft.return_to_frontend)}
                                onChange={(e) =>
                                  setAiSchemaDraft((prev) => ({
                                    ...prev,
                                    return_to_frontend: e.target.checked,
                                  }))
                                }
                                disabled={schemaTypeActionLoading}
                              />
                            }
                            label="Return to Frontend"
                          />
                        </Paper>
                      )}

                      {aiSchemaTab === 4 && (
                        <Paper variant="outlined" sx={{ p: 2, maxWidth: 860 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                            Draft Details
                          </Typography>
                          <TextField
                            fullWidth
                            label="Draft Description"
                            value={aiSchemaDraft.description}
                            onChange={(e) =>
                              setAiSchemaDraft((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                            disabled={schemaTypeActionLoading}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Notes"
                            value={aiSchemaDraft.notes}
                            onChange={(e) =>
                              setAiSchemaDraft((prev) => ({ ...prev, notes: e.target.value }))
                            }
                            multiline
                            minRows={8}
                            disabled={schemaTypeActionLoading}
                          />
                        </Paper>
                      )}
                    </Box>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  borderLeft: { xs: 0, lg: 1 },
                  borderTop: { xs: 1, lg: 0 },
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Chat
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {aiSchemaChatMessages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No messages yet.
                    </Typography>
                  ) : (
                    aiSchemaChatMessages.map((message, index) => {
                      const restorePoint = getRestorePointForMessage(message);
                      const markerPosition =
                        message.restorePointPosition ||
                        (message.role === "assistant" ? "after" : "before");

                      return (
                        <React.Fragment key={`${message.role}-${index}`}>
                          {originalRestorePoint && index === firstUserMessageIndex
                            ? renderRestorePointMarker(originalRestorePoint)
                            : null}
                          {restorePoint && markerPosition !== "after"
                            ? renderRestorePointMarker(restorePoint)
                            : null}
                          <Box
                            sx={{
                              alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                              maxWidth: "92%",
                              p: 1.25,
                              borderRadius: 1,
                              bgcolor:
                                message.role === "user" ? "primary.main" : "background.default",
                              color:
                                message.role === "user"
                                  ? "primary.contrastText"
                                  : "text.primary",
                              border: message.role === "user" ? 0 : 1,
                              borderColor: "divider",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                              {message.role === "user" ? "You" : "AI"}
                            </Typography>
                            <Typography variant="body2">{message.content}</Typography>
                          </Box>
                          {restorePoint && markerPosition === "after"
                            ? renderRestorePointMarker(restorePoint)
                            : null}
                        </React.Fragment>
                      );
                    })
                  )}
                  {aiSchemaGenerating && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={34} />
                      <Typography variant="body2" color="text.secondary">
                        Updating draft...
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Schema Type"
                    value={aiSchemaTypeName}
                    onChange={(e) => setAiSchemaTypeName(e.target.value)}
                    placeholder="e.g. CostBreakdownJson"
                    disabled={
                      aiSchemaMode === "edit" || aiSchemaGenerating || schemaTypeActionLoading
                    }
                    sx={{ mb: 1.25 }}
                  />
                  {aiSchemaDraft && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.25 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}
                      >
                        Change Only
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={allAiChangeScopesSelected}
                              onChange={handleAiChangeScopeAllToggle}
                              disabled={aiSchemaGenerating || schemaTypeActionLoading}
                              sx={{ p: 0.35 }}
                            />
                          }
                          label="All"
                          sx={{
                            m: 0,
                            mr: 0.75,
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.72rem",
                              lineHeight: 1.2,
                              color: "text.secondary",
                            },
                          }}
                        />
                        {AI_CHANGE_SCOPE_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox
                                size="small"
                                checked={aiChangeScope.includes(option.value)}
                                onChange={() => handleAiChangeScopeToggle(option.value)}
                                disabled={aiSchemaGenerating || schemaTypeActionLoading}
                                sx={{ p: 0.35 }}
                              />
                            }
                            label={option.label}
                            sx={{
                              m: 0,
                              mr: 0.75,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.72rem",
                                lineHeight: 1.2,
                                color: "text.secondary",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <TextField
                      fullWidth
                      label={aiSchemaDraft ? "Modify Draft" : "Describe Schema"}
                      value={aiSchemaDescription}
                      onChange={(e) => setAiSchemaDescription(e.target.value)}
                      placeholder={
                        aiSchemaDraft
                          ? "Ask AI to update only the selected sections..."
                          : "Describe fields, validation rules, required outputs, examples..."
                      }
                      disabled={aiSchemaGenerating || schemaTypeActionLoading}
                      multiline
                      minRows={3}
                      maxRows={6}
                    />
                    <Tooltip title={aiSchemaDraft ? "Send Change" : "Generate Draft"}>
                      <span>
                        <IconButton
                          color="primary"
                          onClick={handleGenerateAiSchemaDraft}
                          disabled={
                            !aiSchemaTypeName.trim() ||
                            !aiSchemaDescription.trim() ||
                            (aiSchemaDraft && aiChangeScope.length === 0 && !aiConstraintsRequested) ||
                            aiSchemaGenerating ||
                            schemaTypeActionLoading
                          }
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            flexShrink: 0,
                            "&:hover": { bgcolor: "primary.dark" },
                            "&.Mui-disabled": {
                              bgcolor: "action.disabledBackground",
                              color: "action.disabled",
                            },
                          }}
                        >
                          {aiSchemaGenerating ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : (
                            <SendIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
            <Box sx={{ flex: 1 }}>
              {aiSchemaDraft && (
                <Typography variant="caption" color="text.secondary">
                  {aiSchemaMode === "edit"
                    ? "Review the AI changes, then apply them to the editor and save when ready."
                    : "Review and edit the draft before creating the schema line item."}
                </Typography>
              )}
            </Box>
            <Button
              onClick={() => setAiSchemaDialogOpen(false)}
              disabled={aiSchemaGenerating || schemaTypeActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={
                aiSchemaMode === "edit" ? handleApplyAiSchemaDraftToEditor : handleCreateAiSchemaType
              }
              disabled={!aiSchemaDraft || !aiSchemaTypeName.trim() || schemaTypeActionLoading}
            >
              {schemaTypeActionLoading
                ? "Creating..."
                : aiSchemaMode === "edit"
                  ? "Apply to Editor"
                  : "Create Schema"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={renameSchemaDialogOpen}
          onClose={() => !schemaTypeActionLoading && setRenameSchemaDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Rename Schema</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {selectedTower}/{selectedCommodity} - {selectedSchemaStage}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Schema Type"
              value={renameSchemaTypeName}
              onChange={(e) => setRenameSchemaTypeName(e.target.value)}
              placeholder="e.g. BOMJson"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRenameSchemaDialogOpen(false)}
              disabled={schemaTypeActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRenameSchemaType}
              disabled={
                !renameSchemaTypeName.trim() ||
                renameSchemaTypeName.trim() === selectedSchemaType ||
                schemaTypeActionLoading
              }
            >
              {schemaTypeActionLoading ? "Renaming..." : "Rename"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Schema Type Dialog */}
        <Dialog
          open={duplicateSchemaDialogOpen}
          onClose={() => !schemaTypeActionLoading && setDuplicateSchemaDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Duplicate Schema Type</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {selectedTower}/{selectedCommodity} - {selectedSchemaStage}
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Source Schema</InputLabel>
              <Select
                value={duplicateSourceSchemaType}
                onChange={(e) => setDuplicateSourceSchemaType(e.target.value)}
                label="Source Schema"
              >
                {schemaTypes.map((schemaType) => (
                  <MenuItem key={schemaType.schema_type} value={schemaType.schema_type}>
                    {schemaType.schema_type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="New Schema Type"
              value={duplicateTargetSchemaType}
              onChange={(e) => setDuplicateTargetSchemaType(e.target.value)}
              placeholder="e.g. qualityCheckV2"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={duplicateSchemaDescription}
              onChange={(e) => setDuplicateSchemaDescription(e.target.value)}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDuplicateSchemaDialogOpen(false)}
              disabled={schemaTypeActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDuplicateSchemaType}
              disabled={
                !duplicateSourceSchemaType.trim() ||
                !duplicateTargetSchemaType.trim() ||
                duplicateSourceSchemaType.trim().toLowerCase() ===
                  duplicateTargetSchemaType.trim().toLowerCase() ||
                schemaTypeActionLoading
              }
            >
              {schemaTypeActionLoading ? "Duplicating..." : "Duplicate"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Dialog */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Save New Schema Version</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Version Number"
              type="number"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Version Description"
              multiline
              rows={3}
              value={versionDescription}
              onChange={(e) => setVersionDescription(e.target.value)}
              placeholder="Describe the changes in this version..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSchema} variant="contained" disabled={!newVersion || saving}>
              {saving ? "Saving..." : "Save Version"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Version Dialog */}
        <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
          <DialogTitle>Activate Version</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to activate version {versionToActivate}? This will deactivate
              the current active version.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleActivateVersion} variant="contained" color="primary">
              Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* AI Refine Dialog */}
        <Dialog
          open={refineDialogOpen}
          onClose={() => setRefineDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            AI Refine {refineType === "system" ? "System Prompt" : "User Prompt"}
          </DialogTitle>
          <DialogContent>
            {/* Current Prompt Display */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Current Prompt:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 200,
                  overflow: "auto",
                  bgcolor: "background.default",
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentPromptText}
              </Paper>
            </Box>

            {/* AI Variants */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                AI-Generated Variants (select one to apply):
              </Typography>
              {loadingVariants ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Generating 3 refined variants of your prompt...
                  </Typography>
                </Box>
              ) : aiVariants.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {aiVariants.map((variant, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        border: 2,
                        borderColor: selectedVariant === variant ? "primary.main" : "divider",
                        bgcolor:
                          selectedVariant === variant ? "primary.lighter" : "background.paper",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.lighter",
                        },
                      }}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Box
                          sx={{
                            mt: 0.5,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: 2,
                            borderColor: selectedVariant === variant ? "primary.main" : "divider",
                            bgcolor: selectedVariant === variant ? "primary.main" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {selectedVariant === variant && (
                            <CheckCircleIcon sx={{ fontSize: 16, color: "white" }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                          >
                            Variant {index + 1}
                            {index === 0 && " - Concise & Clear"}
                            {index === 1 && " - Detailed & Examples"}
                            {index === 2 && " - Structured & Organized"}
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              maxHeight: 150,
                              overflow: "auto",
                              bgcolor: "background.default",
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {variant}
                          </Paper>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No variants available
                </Typography>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              💡 Select one variant above and click "Apply Selected Variant"
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefineDialogOpen(false)} disabled={refining}>
              Cancel
            </Button>
            <Button
              onClick={handleRefinePrompt}
              variant="contained"
              color="primary"
              disabled={!selectedVariant || refining}
              startIcon={refining ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              {refining ? "Applying..." : "Apply Selected Variant"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Constraint Master Create/Edit Dialog */}
        <Dialog
          open={constraintMasterDialogOpen}
          onClose={handleConstraintMasterDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {constraintMasterDialogMode === "edit"
              ? "Edit Constraint Category"
              : constraintMasterCreateType === "target"
                ? "Add Target Column"
                : "Add Constraint Category"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mt: 1 }}>
              <TextField
                label="Component"
                value={constraintMasterForm.component}
                onChange={(e) => handleConstraintMasterFieldChange("component", e.target.value)}
                size="small"
                fullWidth
                disabled
                helperText="Uses the selected schema from the left panel"
              />
              <TextField
                label="Target Column"
                value={constraintMasterForm.target_field}
                onChange={(e) => handleConstraintMasterFieldChange("target_field", e.target.value)}
                size="small"
                fullWidth
                disabled={savingConstraintMaster}
              />
              <TextField
                label="Source Category"
                value={constraintMasterForm.source_category}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("source_category", e.target.value)
                }
                size="small"
                fullWidth
                disabled={savingConstraintMaster}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!constraintMasterForm.is_active}
                    onChange={(e) =>
                      handleConstraintMasterFieldChange("is_active", e.target.checked)
                    }
                    disabled={savingConstraintMaster}
                  />
                }
                label="Active"
              />
              <TextField
                label="Allowed Values"
                value={constraintMasterForm.allowed_values}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("allowed_values", e.target.value)
                }
                size="small"
                fullWidth
                disabled={savingConstraintMaster}
                helperText="Optional comma-separated values"
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <TextField
                  label="Min"
                  type="number"
                  value={constraintMasterForm.min_value}
                  onChange={(e) =>
                    handleConstraintMasterFieldChange("min_value", e.target.value)
                  }
                  size="small"
                  fullWidth
                  disabled={savingConstraintMaster}
                />
                <TextField
                  label="Max"
                  type="number"
                  value={constraintMasterForm.max_value}
                  onChange={(e) =>
                    handleConstraintMasterFieldChange("max_value", e.target.value)
                  }
                  size="small"
                  fullWidth
                  disabled={savingConstraintMaster}
                />
              </Box>
              <TextField
                label="Guidance"
                value={constraintMasterForm.guidance_text}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("guidance_text", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                minRows={3}
                disabled={savingConstraintMaster}
                sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}
              />
              <TextField
                label="Field Description"
                value={constraintMasterForm.field_description}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("field_description", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={savingConstraintMaster}
              />
              <TextField
                label="Selection Guidance"
                value={constraintMasterForm.selection_guidance}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("selection_guidance", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={savingConstraintMaster}
              />
              <TextField
                label="Decision Inputs"
                value={constraintMasterForm.decision_inputs}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("decision_inputs", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={savingConstraintMaster}
              />
              <TextField
                label="Fallback Guidance"
                value={constraintMasterForm.fallback_guidance}
                onChange={(e) =>
                  handleConstraintMasterFieldChange("fallback_guidance", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={savingConstraintMaster}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConstraintMasterDialogClose} disabled={savingConstraintMaster}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConstraintMasterDialogSave}
              disabled={
                savingConstraintMaster ||
                !constraintMasterForm.component.trim() ||
                !constraintMasterForm.target_field.trim() ||
                !constraintMasterForm.source_category.trim() ||
                (constraintMasterDialogMode === "edit" && !constraintMasterDialogIsDirty)
              }
              startIcon={savingConstraintMaster ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {savingConstraintMaster ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Fixed Position Alerts (Bottom Overlay) */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              minWidth: 300,
              maxWidth: 500,
              zIndex: 9999,
              boxShadow: 3,
            }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              minWidth: 300,
              maxWidth: 500,
              zIndex: 9999,
              boxShadow: 3,
            }}
          >
            {success}
          </Alert>
        )}

        {/* New Rule Dialog */}
        <Dialog
          open={newRuleDialogOpen}
          onClose={() => setNewRuleDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Constraint Rule</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField
                label="Priority"
                type="number"
                value={newRuleData.priority}
                onChange={(e) =>
                  setNewRuleData({ ...newRuleData, priority: parseInt(e.target.value) || 100 })
                }
                size="small"
                fullWidth
                helperText="Lower number = higher precedence"
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2">Active:</Typography>
                <Switch
                  checked={newRuleData.active}
                  onChange={(e) => setNewRuleData({ ...newRuleData, active: e.target.checked })}
                />
              </Box>

              <TextField
                label="Notes"
                value={newRuleData.notes}
                onChange={(e) => setNewRuleData({ ...newRuleData, notes: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={2}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Condition (JSON)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Define when this rule applies. Example:{" "}
                  {JSON.stringify({ marketPositioning: "Premium" })}
                </Typography>
                <TextField
                  value={JSON.stringify(newRuleData.condition_json, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setNewRuleData({ ...newRuleData, condition_json: parsed });
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                  multiline
                  rows={4}
                  fullWidth
                  size="small"
                  sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Constraints (JSON)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Define the constraints to apply. Example:{" "}
                  {JSON.stringify({ required: ["field1"], enum: { status: ["active"] } })}
                </Typography>
                <TextField
                  value={JSON.stringify(newRuleData.constraints_json, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setNewRuleData({ ...newRuleData, constraints_json: parsed });
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                  multiline
                  rows={8}
                  fullWidth
                  size="small"
                  sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewRuleDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={createNewRule}>
              Create Rule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Execution Group Management Dialog */}
        <Dialog
          open={executionGroupDialogOpen}
          onClose={() => !savingGroups && setExecutionGroupDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">Manage Execution Groups</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={autoAssignGroups}
                disabled={savingGroups}
              >
                Auto-Assign Groups
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reorder schemas and assign execution groups. Schemas in the same group execute in
              parallel.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>Order</TableCell>
                    <TableCell>Schema Type</TableCell>
                    <TableCell width={120}>Group</TableCell>
                    <TableCell width={100} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tempSchemaGroups.map((schema, index) => (
                    <TableRow key={schema.schema_type}>
                      <TableCell>
                        <Chip
                          label={index + 1}
                          size="small"
                          sx={{
                            minWidth: 40,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {schema.schema_type}
                        </Typography>
                        {schema.description && (
                          <Typography variant="caption" color="text.secondary">
                            {schema.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={schema.execution_group}
                          onChange={(e) => handleGroupChange(schema.schema_type, e.target.value)}
                          size="small"
                          inputProps={{ min: 1, max: 99 }}
                          disabled={savingGroups}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <IconButton
                            size="small"
                            onClick={() => moveSchemaUp(index)}
                            disabled={index === 0 || savingGroups}
                          >
                            <ExpandMoreIcon sx={{ transform: "rotate(180deg)" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveSchemaDown(index)}
                            disabled={index === tempSchemaGroups.length - 1 || savingGroups}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, p: 2, bgcolor: "info.lighter", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 Tip: Schemas are executed by group number (ascending). Schemas in the same group
                run in parallel. Use the up/down arrows to reorder, or change group numbers
                directly.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExecutionGroupDialogOpen(false)} disabled={savingGroups}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveExecutionGroups}
              disabled={savingGroups}
              startIcon={savingGroups ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {savingGroups ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
};

export default SchemaManagementV3Page;

